import React, { useState, useEffect, useMemo, memo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import { 
    getAuth, 
    onAuthStateChanged, 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signOut
} from 'firebase/auth';
import { 
    getFirestore, 
    collection, 
    onSnapshot, 
    addDoc, 
    doc, 
    writeBatch,
    serverTimestamp,
    query,
    orderBy,
    runTransaction,
    updateDoc,
    deleteDoc,
    setDoc,
    getDocs,
    where,
    getDoc
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { ArrowLeftRight, Package, History, UserPlus, PackagePlus, ChevronDown, AlertTriangle, Building, LayoutDashboard, Filter, XCircle, Sparkles, BrainCircuit, FolderTree, Edit, Trash2, LogOut, KeyRound, CheckCircle2, ShieldCheck, User, PlusCircle, FileText, Search, Settings, Users, Library, UploadCloud, Heart, Warehouse, FileSpreadsheet } from 'lucide-react';

// --- Firebase Configuration ---


const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};



const appId = 'trinet-inventory';

// --- Initial Category Data ---
const initialCategoryData = {
    "CCTV Systems": { "CCTV Recorders": {}, "CCTV Servers": {}, "CCTV Cameras": { "Dome": { "Fixed Dome": {}, "Varifocal Dome": {} }, "Bullet": { "Fixed Bullet": {}, "Varifocal Bullet": {} }, "PTZ": {}, "Others": {} }, "Camera Accessories": {} },
    "PA Systems": { "Ceiling Speaker": {}, "Box Speaker": {}, "Amplifier": {}, "EVC Recorders": {}, "System Managers": {}, "Line Supervisory": {} },
    "Access Control System": { "Standalone Controllers": {}, "Controllers": {}, "Card Readers": {}, "Keypad Card Readers": {}, "Exit Push Button": {}, "Exit Sensor": {}, "Door Contact Sensor": {}, "EM Lock (Indoor)": {}, "EM Lock (Outdoor)": {} },
    "Biometric Access Control System": { "UBio Biometric Readers": {}, "Anviz Biometric Readers": {}, "Hik Biometric Readers": {} },
    "Power Supply": { "Card Access Power Supply": {}, "CCTV Power Supply": {}, "24V AC Transformer": {} }
};


// --- Image Resizing Utility ---
const resizeImage = (file, maxWidth = 1024, quality = 0.8) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            
            let { width, height } = img;
            if (width > height) {
                if (width > maxWidth) {
                    height *= maxWidth / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxWidth) {
                    width *= maxWidth / height;
                    height = maxWidth;
                }
            }
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
                if (blob) {
                    // Create a new file with a consistent name and type
                    resolve(new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    }));
                } else {
                    reject(new Error('Canvas to Blob conversion failed'));
                }
            }, 'image/jpeg', quality);
        };
        img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
});


// --- Reusable UI Components ---

const PrimaryButton = memo(({ children, onClick, type = 'button', className = '', disabled = false }) => (
    <button type={type} onClick={onClick} disabled={disabled} className={`flex items-center justify-center space-x-2 w-full px-4 py-2 text-white font-semibold rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-transform transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed ${className}`}>{children}</button>
));

const ConfirmModal = ({ title, message, onConfirm, onCancel, confirmText = "Confirm", confirmColor = "bg-red-600 hover:bg-red-700" }) => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-scale">
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-600 mt-2 mb-4">{message}</p>
            <div className="flex space-x-2 justify-end">
                <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300">
                    Cancel
                </button>
                 <PrimaryButton onClick={onConfirm} className={`${confirmColor} w-auto`}>
                    {confirmText}
                </PrimaryButton>
            </div>
        </div>
    </div>
);

const ImageViewerModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <button onClick={onClose} className="absolute top-4 right-4 text-white text-3xl">&times;</button>
            <img src={imageUrl} alt="Full size view" className="max-w-full max-h-full rounded-lg" onClick={e => e.stopPropagation()} />
        </div>
    );
};

const FormInput = memo(({ label, value, onChange, placeholder, type = 'text', required = true, name = '', ...props }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input name={name || label.toLowerCase()} type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900" {...props} />
    </div>
));

const FormSelect = memo(({ label, value, onChange, children, required = true, name = '' }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <select name={name || label.toLowerCase()} value={value} onChange={onChange} required={required} className="w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900">{children}</select>
    </div>
));

const SearchableSelect = memo(({ options, value, onChange, placeholder, label, renderOption }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);
    
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option => 
            option.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            (option.sku && option.sku.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [options, searchTerm]);

    const selectedOption = useMemo(() => options.find(o => o.id === value), [options, value]);

    return (
        <div className="relative" ref={wrapperRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="relative">
                <button
                    type="button"
                    className="w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm text-left flex justify-between items-center focus:ring-indigo-500 focus:border-indigo-500"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedOption ? selectedOption.name : placeholder}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
                </button>
            </div>
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border max-h-60 overflow-hidden flex flex-col">
                    <div className="p-2 border-b">
                         <input
                            type="text"
                            placeholder="Search..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <ul className="overflow-y-auto">
                        {filteredOptions.length > 0 ? filteredOptions.map(option => (
                             <li
                                key={option.id}
                                className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${value === option.id ? 'bg-indigo-100' : ''}`}
                                onClick={() => {
                                    onChange(option.id);
                                    setIsOpen(false);
                                    setSearchTerm('');
                                }}
                            >
                                {renderOption(option)}
                            </li>
                        )) : (
                            <li className="px-4 py-2 text-gray-500">No results found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
});

const EditUserModal = memo(({ user, onSaveUser, onCancel, onSendPasswordReset, allCategories }) => {
    const [role, setRole] = useState(user.role);
    const [managedCategories, setManagedCategories] = useState(user.managedCategories || []);
    const [isSending, setIsSending] = useState(false);
    const [sendSuccess, setSendSuccess] = useState(false);

    const handleCategoryToggle = (categoryId) => {
        setManagedCategories(prev => 
            prev.includes(categoryId) 
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleSave = () => {
        const newPermissions = role === 'normal' ? managedCategories : [];
        onSaveUser(user.id, role, newPermissions);
        onCancel();
    };

    const handlePasswordResetClick = async () => {
        setIsSending(true);
        const success = await onSendPasswordReset(user.id);
        setIsSending(false);
        if (success) {
            setSendSuccess(true);
            setTimeout(() => {
                onCancel();
            }, 3000);
        }
    };

    const topLevelCategories = useMemo(() => allCategories.filter(c => !c.parentId), [allCategories]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={onCancel}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-fade-in-scale" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Edit User</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">Editing user: <span className="font-medium text-gray-900">{user.id}</span></p>
                    <FormSelect label="User Role" value={role} onChange={e => setRole(e.target.value)}>
                        <option value="basic">Basic User (Read-only)</option>
                        <option value="normal">Normal User (Can move stock)</option>
                        <option value="admin">Admin User (Full access)</option>
                    </FormSelect>

                    {role === 'normal' && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Managed Stock Categories</label>
                            <div className="p-2 border rounded-md max-h-40 overflow-y-auto">
                                {topLevelCategories.map(cat => (
                                    <div key={cat.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`cat-${cat.id}`}
                                            checked={managedCategories.includes(cat.id)}
                                            onChange={() => handleCategoryToggle(cat.id)}
                                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <label htmlFor={`cat-${cat.id}`} className="ml-2 block text-sm text-gray-900">{cat.name}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                     {!sendSuccess ? (
                        <button 
                            type="button" 
                            onClick={handlePasswordResetClick}
                            disabled={isSending}
                            className="w-full px-4 py-2 bg-yellow-500 text-white font-semibold rounded-md hover:bg-yellow-600 disabled:bg-gray-400"
                        >
                            {isSending ? 'Sending...' : 'Send Password Reset Email'}
                        </button>
                    ) : (
                        <div className="text-center p-3 bg-green-100 text-green-800 rounded-md">
                            <p className="font-semibold">Email Sent!</p>
                            <p className="text-sm">Please check the inbox for {user.id}.</p>
                        </div>
                    )}
                    <div className="flex space-x-2 pt-2">
                        <PrimaryButton onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700">Save Changes</PrimaryButton>
                        <button type="button" onClick={onCancel} className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
});

const UserManager = memo(({ onAddUser, users, onDeleteUser, onSaveUser, currentUserEmail, onEditUser, allCategories }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState('basic');
    const [error, setError] = useState(''); // Local error state for the form
    const [success, setSuccess] = useState(''); // Local success state for the form

    const handleAddSubmit = async (e) => { // Make async to await the result
        e.preventDefault();
        setError(''); // Clear previous errors
        setSuccess(''); // Clear previous success messages

        if (!email.trim() || !password.trim()) {
            setError("Email and password cannot be empty.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        const errorMessage = await onAddUser(email, password, role);

        if (errorMessage) {
            setError(errorMessage); // Display error from Firebase or validation
        } else {
            // Clear form and show success message
            setSuccess(`Successfully created user: ${email}`);
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setRole('basic');
            setTimeout(() => setSuccess(''), 5000); // Clear message after 5 seconds
        }
    };

    return (
        <div>
            <form onSubmit={handleAddSubmit} className="space-y-4 mb-6 pb-6 border-b">
                <h3 className="font-semibold text-lg text-gray-800">Add New User</h3>
                <FormInput 
                    label="User Email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    placeholder="user@company.com" 
                />
                <FormInput 
                    label="Password" 
                    type="password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    placeholder="Min. 6 characters" 
                />
                <FormInput 
                    label="Confirm Password" 
                    type="password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    placeholder="Re-enter password" 
                />
                <FormSelect label="User Role" value={role} onChange={e => setRole(e.target.value)}>
                    <option value="basic">Basic User (Read-only)</option>
                    <option value="normal">Normal User (Can move stock)</option>
                    <option value="admin">Admin User (Full access)</option>
                </FormSelect>
                {error && <p className="text-red-500 text-sm text-center bg-red-50 p-2 rounded-md">{error}</p>}
                {success && <p className="text-green-500 text-sm text-center bg-green-50 p-2 rounded-md">{success}</p>}
                <PrimaryButton type="submit" className="bg-teal-600 hover:bg-teal-700">
                    <UserPlus className="w-5 h-5" />
                    <span>Add User</span>
                </PrimaryButton>
            </form>

            <h3 className="font-semibold text-lg text-gray-800 mb-2">Authorized Users</h3>
            <div className="max-h-96 overflow-y-auto">
                <ul className="divide-y divide-gray-200">
                    {users.map(user => (
                        <li key={user.id} className="flex flex-col sm:flex-row justify-between sm:items-center py-3 px-1 space-y-2 sm:space-y-0">
                            <span className="text-sm text-gray-700 truncate" title={user.id}>{user.id}</span>
                            <div className="flex items-center space-x-2">
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">{user.role}</span>
                                <button 
                                    onClick={() => onEditUser(user)}
                                    className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                                    title={`Edit ${user.id}`}
                                >
                                    <Edit size={16} />
                                </button>
                                <button 
                                    onClick={() => onDeleteUser(user.id)} 
                                    className="p-1.5 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-100 transition-colors disabled:text-gray-300 disabled:cursor-not-allowed" 
                                    title={`Delete ${user.id}`}
                                    disabled={user.id === currentUserEmail}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </li>
                    ))}
                    {users.length === 0 && <li className="p-4 text-center text-gray-500">No authorized users found.</li>}
                </ul>
            </div>
        </div>
    );
});

const Card = memo(({ children, title, icon, className = '' }) => {
    const IconComponent = icon;
    return (
        <div className={`bg-white rounded-xl shadow-md flex flex-col overflow-hidden ${className}`}>
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center shrink-0">
                <div className="flex items-center space-x-3">
                    <IconComponent className="w-6 h-6 text-indigo-500" />
                    <h2 className="text-lg font-bold text-gray-800">{title}</h2>
                </div>
            </div>
            <div className="p-4 md:p-6 relative flex flex-col flex-grow min-h-0">{children}</div>
        </div>
    );
});

const FormTextarea = memo(({ label, value, onChange, placeholder, required = false, name = '' }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <textarea name={name || label.toLowerCase()} value={value} onChange={onChange} placeholder={placeholder} required={required} rows="2" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900" />
    </div>
));

const EditStockItemModal = memo(({ item, categoryTree, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: item.name || '',
        sku: item.sku || '',
        quantityInStock: item.quantityInStock ?? 0,
        categoryId: item.categoryId || '',
        alertThreshold: item.alertThreshold ?? 10
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        const isNumeric = ['quantityInStock', 'alertThreshold'].includes(name);
        setFormData(prev => ({ ...prev, [name]: isNumeric ? (value === '' ? '' : parseInt(value, 10)) : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(item.id, formData);
    };

    const generateHierarchicalOptions = (categories, level = 0) => {
        let options = [];
        categories.forEach(category => {
            options.push(<option key={category.id} value={category.id}>{'\u00A0\u00A0'.repeat(level)} {category.name}</option>);
            if (category.children?.length > 0) {
                options = options.concat(generateHierarchicalOptions(category.children, level + 1));
            }
        });
        return options;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={onCancel}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-fade-in-scale" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Edit Stock Item</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <FormInput label="Item Name" name="name" value={formData.name} onChange={handleChange} />
                    <FormInput label="SKU / Part Number" name="sku" value={formData.sku} onChange={handleChange} />
                    <FormInput label="Current Quantity" name="quantityInStock" type="number" value={formData.quantityInStock} onChange={handleChange} min="0" />
                    <FormInput label="Low Stock Threshold" name="alertThreshold" type="number" value={formData.alertThreshold} onChange={handleChange} min="0" />
                    <FormSelect label="Category" name="categoryId" value={formData.categoryId} onChange={handleChange}>
                        <option value="">-- Select a Category --</option>
                        {generateHierarchicalOptions(categoryTree)}
                    </FormSelect>
                    <div className="flex space-x-2 pt-2">
                        <PrimaryButton type="submit" className="bg-indigo-600 hover:bg-indigo-700">Save Changes</PrimaryButton>
                        <button type="button" onClick={onCancel} className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

const EditSimpleItemModal = memo(({ item, title, onSave, onCancel }) => {
    const [name, setName] = useState(item.name || '');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(item.id, name);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={e => e.stopPropagation()}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-fade-in-scale" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Edit {title}</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <FormInput label="Name" value={name} onChange={e => setName(e.target.value)} />
                    <div className="flex space-x-2 pt-2">
                        <PrimaryButton type="submit" className="bg-indigo-600 hover:bg-indigo-700">Save Changes</PrimaryButton>
                        <button type="button" onClick={onCancel} className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

const CategoryTreeItem = memo(({ category, onEdit, onDelete }) => (
    <li className="ml-4 border-l pl-2 py-1">
        <div className="flex justify-between items-center group">
            <div>
                <span className="text-sm text-gray-700">{category.name}</span>
                {category.remarks && <span className="text-xs text-gray-500 italic ml-2"> - {category.remarks}</span>}
            </div>
            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onEdit(category)} className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-100" title={`Edit ${category.name}`}><Edit size={14} /></button>
                <button onClick={() => onDelete(category.id)} className="p-1.5 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-100" title={`Delete ${category.name}`}><Trash2 size={14} /></button>
            </div>
        </div>
        {category.children && category.children.length > 0 && (
            <ul>{category.children.map(child => <CategoryTreeItem key={child.id} category={child} onEdit={onEdit} onDelete={onDelete} />)}</ul>
        )}
    </li>
));


// --- Management Components ---
const CategoryManager = memo(({ categories, onAdd, onUpdate, onDelete, onSeed, isSeeding, stockItems, categoryTree }) => {
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryParent, setNewCategoryParent] = useState('root');
    const [newCategoryRemarks, setNewCategoryRemarks] = useState('');

    const [editingCategory, setEditingCategory] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');
    const [editCategoryParent, setEditCategoryParent] = useState('');
    const [editCategoryRemarks, setEditCategoryRemarks] = useState('');

    const handleEditClick = (category) => {
        setEditingCategory(category);
        setEditCategoryName(category.name);
        setEditCategoryParent(category.parentId || 'root');
        setEditCategoryRemarks(category.remarks || '');
    };

    const handleCancelEdit = () => {
        setEditingCategory(null);
    };

    const handleAddSubmit = (e) => {
        e.preventDefault();
        onAdd(newCategoryName, newCategoryParent, newCategoryRemarks);
        setNewCategoryName('');
        setNewCategoryParent('root');
        setNewCategoryRemarks('');
    };

    const handleUpdateSubmit = (e) => {
        e.preventDefault();
        onUpdate(editingCategory.id, editCategoryName, editCategoryParent, editCategoryRemarks);
        setEditingCategory(null);
    };
    
    const handleDeleteClick = (categoryId) => {
        const itemsInCategory = stockItems.some(item => item.categoryId === categoryId);
        const childCategories = categories.some(cat => cat.parentId === categoryId);
        if (itemsInCategory || childCategories) {
            alert("Cannot delete category. It contains stock items or sub-categories.");
            return;
        }
        onDelete(categoryId);
    };

    const getDescendantIds = (parentId) => {
        const children = categories.filter(c => c.parentId === parentId);
        let descendantIds = children.map(c => c.id);
        children.forEach(child => descendantIds = [...descendantIds, ...getDescendantIds(child.id)]);
        return descendantIds;
    };
    
    const generateHierarchicalOptions = (categories, level = 0, excludedIds = []) => {
        let options = [];
        categories.forEach(category => {
            if (excludedIds.includes(category.id)) return;
            options.push(<option key={category.id} value={category.id}>{'\u00A0\u00A0'.repeat(level)} {category.name}</option>);
            if (category.children?.length > 0) {
                options = options.concat(generateHierarchicalOptions(category.children, level + 1, excludedIds));
            }
        });
        return options;
    };

    const excludedIdsForEdit = editingCategory ? [editingCategory.id, ...getDescendantIds(editingCategory.id)] : [];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                {!editingCategory ? (
                    <form onSubmit={handleAddSubmit} className="space-y-4">
                        <h3 className="font-semibold text-lg text-gray-800">Add New Category</h3>
                        <FormInput label="New Category Name" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} />
                        <FormSelect label="Parent Category" value={newCategoryParent} onChange={e => setNewCategoryParent(e.target.value)}>
                            <option value="root">-- Main Category --</option>
                            {generateHierarchicalOptions(categoryTree)}
                        </FormSelect>
                        <FormInput label="Remarks (optional)" value={newCategoryRemarks} onChange={e => setNewCategoryRemarks(e.target.value)} required={false}/>
                        <PrimaryButton type="submit" className="bg-indigo-600 hover:bg-indigo-700">Add Category</PrimaryButton>
                    </form>
                ) : (
                    <form onSubmit={handleUpdateSubmit} className="space-y-4">
                        <h3 className="font-semibold text-lg text-gray-800">Edit Category</h3>
                        <FormInput label="Category Name" value={editCategoryName} onChange={e => setEditCategoryName(e.target.value)} />
                        <FormSelect label="Parent Category" value={editCategoryParent} onChange={e => setEditCategoryParent(e.target.value)}>
                            <option value="root">-- Main Category --</option>
                            {generateHierarchicalOptions(categoryTree, 0, excludedIdsForEdit)}
                        </FormSelect>
                        <FormInput label="Remarks (optional)" value={editCategoryRemarks} onChange={e => setEditCategoryRemarks(e.target.value)} required={false}/>
                        <div className="flex space-x-2">
                            <PrimaryButton type="submit" className="bg-indigo-600 hover:bg-indigo-700">Save Changes</PrimaryButton>
                            <button type="button" onClick={handleCancelEdit} className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300">Cancel</button>
                        </div>
                    </form>
                )}
            </div>
            <div>
                <h3 className="font-semibold text-lg text-gray-800 mb-2">Category Structure</h3>
                {categories.length === 0 ? (
                    <div className="text-center p-4 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-600 mb-2">No categories found.</p>
                        <button onClick={onSeed} disabled={isSeeding} className="text-sm text-indigo-600 hover:underline disabled:text-gray-400">
                            {isSeeding ? 'Adding...' : 'Initialize with default categories?'}
                        </button>
                    </div>
                ) : (
                    <div className="max-h-96 overflow-y-auto border p-2 rounded-md">
                        <ul>{categoryTree.map(cat => <CategoryTreeItem key={cat.id} category={cat} onEdit={handleEditClick} onDelete={handleDeleteClick} />)}</ul>
                    </div>
                )}
            </div>
        </div>
    );
});

const StockItemManager = memo(({ categories, onAdd, userPermissions }) => {
    const [name, setName] = useState('');
    const [sku, setSku] = useState('');
    const [quantity, setQuantity] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [alertThreshold, setAlertThreshold] = useState('10');
    const [imageFiles, setImageFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(null);

    const permittedCategories = useMemo(() => {
        if (userPermissions.role === 'admin') {
            return categories;
        }
        
        if (userPermissions.role !== 'normal' || !userPermissions.managedCategories || userPermissions.managedCategories.length === 0 || !categories || categories.length === 0) {
            return [];
        }

        const getDescendantIds = (parentId) => {
            const descendants = [];
            const children = categories.filter(c => c.parentId === parentId);
            for (const child of children) {
                descendants.push(child.id);
                descendants.push(...getDescendantIds(child.id));
            }
            return descendants;
        };

        const allowedSet = new Set(userPermissions.managedCategories);

        userPermissions.managedCategories.forEach(catId => {
            const descendantIds = getDescendantIds(catId);
            descendantIds.forEach(id => allowedSet.add(id));
        });
        
        return categories.filter(cat => allowedSet.has(cat.id));

    }, [categories, userPermissions]);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        try {
            // Resize all selected images before setting them in state
            const resizedFiles = await Promise.all(files.map(file => resizeImage(file)));
            setImageFiles(resizedFiles);
        } catch (err) {
            console.error("Image resizing failed:", err);
            // Fallback to original files if resizing fails
            setImageFiles(files);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onAdd(name, sku, quantity, selectedCategory, alertThreshold, imageFiles, setUploadProgress);
        setName('');
        setSku('');
        setQuantity('');
        setSelectedCategory('');
        setAlertThreshold('10');
        setImageFiles([]);
        setUploadProgress(null);
    };

    const CategorySelector = ({ selectedCategory, onCategoryChange }) => {
        const topLevel = permittedCategories.filter(c => !c.parentId || !permittedCategories.some(p => p.id === c.parentId));
        const getChildren = (parentId) => permittedCategories.filter(c => c.parentId === parentId);
        
        const permittedCategoriesMap = useMemo(() => {
            const map = new Map();
            for (const cat of permittedCategories) {
                map.set(cat.id, cat);
            }
            return map;
        // FROM: }, [permittedCategories]);
        // TO:
        }, []); // This memo now correctly depends only on the initial render of permittedCategories within this scope.
        
        const findPath = useCallback((catId) => {
            const path = [];
            let current = permittedCategoriesMap.get(catId);
            while (current) {
                path.unshift(current);
                current = permittedCategoriesMap.get(current.parentId);
            }
            return path;
        }, [permittedCategoriesMap]);

        const path = selectedCategory ? findPath(selectedCategory) : [];
        const selectors = [];
        let currentLevelCats = topLevel;

        for (let i = 0; i < path.length + 1; i++) {
            if (currentLevelCats.length === 0 && i <= path.length) break;
            selectors.push(
                <FormSelect key={i} label={`Level ${i + 1} Category`} value={path[i]?.id || ''} onChange={(e) => onCategoryChange(e.target.value)}>
                    <option value="">-- Select --</option>
                    {currentLevelCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </FormSelect>
            );
            if (path[i]) {
                currentLevelCats = getChildren(path[i].id);
            } else {
                break;
            }
        }
        return permittedCategories.length > 0 ? (
             <div className="space-y-4 p-2 border rounded-md bg-gray-50">{selectors}</div>
        ) : (
            <div className="p-4 text-center bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                You do not have permission to add stock to any categories. Please contact an administrator.
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-lg mx-auto">
            <FormInput label="Item Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Dome Camera" />
            <FormInput label="SKU / Part Number" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="e.g., Model Name/ Initialism" />
            <CategorySelector selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />
            <FormInput label="Initial Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="e.g., 100" type="number" min="1" />
            <FormInput label="Low Stock Threshold" value={alertThreshold} onChange={(e) => setAlertThreshold(e.target.value)} placeholder="e.g., 10" type="number" min="0" />
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Photos (Optional)</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                        <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                            <label htmlFor="stock-file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                                <span>Upload files</span>
                                <input id="stock-file-upload" name="stock-file-upload" type="file" className="sr-only" multiple onChange={handleFileChange} accept="image/*" />
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                </div>
                 {imageFiles.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        {Array.from(imageFiles).map((file, index) => (
                            <img key={index} src={URL.createObjectURL(file)} alt={`preview ${index}`} className="h-24 w-full object-cover rounded-md" />
                        ))}
                    </div>
                )}
                {uploadProgress !== null && (
                    <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                        <p className="text-xs text-center text-gray-600 mt-1">{uploadProgress < 100 ? `Uploading... ${Math.round(uploadProgress)}%` : 'Upload Complete!'}</p>
                    </div>
                )}
            </div>

            <PrimaryButton type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={permittedCategories.length === 0 || (uploadProgress !== null && uploadProgress < 100)}>Add Stock Item</PrimaryButton>
        </form>
    );
});

const AdvancedManager = memo(({ title, items, onAdd, onEditItem, onDelete, icon }) => {
    const [name, setName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const Icon = icon;

    const handleAddSubmit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onAdd(name);
        setName('');
    };
    
    const filteredItems = useMemo(() => {
        if (!searchTerm) return items;
        return items.filter(item =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [items, searchTerm]);


    return (
        <div>
            <form onSubmit={handleAddSubmit} className="flex space-x-2 mb-6 pb-6 border-b">
                <div className="flex-grow">
                    <label htmlFor={`add-${title}`} className="sr-only">Add New {title}</label>
                    <input 
                        id={`add-${title}`}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={`New ${title}'s Full Name...`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900"
                    />
                </div>
                <PrimaryButton type="submit" className="bg-indigo-600 hover:bg-indigo-700 w-auto px-3" disabled={!name.trim()}>
                    <Icon className="w-5 h-5"/>
                </PrimaryButton>
            </form>
            
            <div className="mb-4 relative">
                 <label htmlFor={`search-${title}`} className="sr-only">Search {title}s</label>
                 <input
                    id={`search-${title}`}
                    type="text"
                    placeholder={`Search ${title}s...`}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md"
                 />
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>


            <h3 className="font-semibold text-lg text-gray-800 mb-2">{title} List</h3>
            <div className="max-h-96 overflow-y-auto">
                <ul className="divide-y divide-gray-200">
                    {filteredItems.map(item => (
                        <li key={item.id} className="flex justify-between items-center py-3 px-1 group">
                             <span className="text-sm text-gray-700">{item.name}</span>
                             <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => onEditItem(item)} className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-100" title={`Edit ${item.name}`}>
                                    <Edit size={16} />
                                </button>
                                <button onClick={() => onDelete(item.id)} className="p-1.5 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-100" title={`Delete ${item.name}`}>
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </li>
                    ))}
                    {filteredItems.length === 0 && <li className="p-4 text-center text-gray-500">No {title}s found.</li>}
                </ul>
            </div>
        </div>
    );
});

const StockMovementManager = memo(({ technicians, stockItems, customers, onMove }) => {
    const [selectedTechnician, setSelectedTechnician] = useState('');
    const [selectedStockItem, setSelectedStockItem] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [movementQuantity, setMovementQuantity] = useState('');
    const [updatedBy, setUpdatedBy] = useState('');
    const [workOrderNumber, setWorkOrderNumber] = useState('');
    const [serialNumbers, setSerialNumbers] = useState('');
    const [remarks, setRemarks] = useState('');
    const [imageFiles, setImageFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(null);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        try {
            // Resize all selected images before setting them in state
            const resizedFiles = await Promise.all(files.map(file => resizeImage(file)));
            setImageFiles(resizedFiles);
        } catch (err) {
            console.error("Image resizing failed:", err);
            // Fallback to original files if resizing fails
            setImageFiles(files);
        }
    };

    const handleMovement = async (type) => {
        const success = await onMove(type, { selectedTechnician, selectedStockItem, movementQuantity, selectedCustomer, updatedBy, workOrderNumber, serialNumbers, remarks, imageFiles }, setUploadProgress);
        if (success) {
            setSelectedTechnician('');
            setSelectedStockItem('');
            setMovementQuantity('');
            setSelectedCustomer('');
            setUpdatedBy('');
            setWorkOrderNumber('');
            setSerialNumbers('');
            setRemarks('');
            setImageFiles([]);
            setUploadProgress(null);
        }
    };
    
    return (
        <div className="space-y-4 max-w-lg mx-auto">
            <SearchableSelect
                label="Select Technician"
                placeholder="-- Choose a technician --"
                options={technicians}
                value={selectedTechnician}
                onChange={setSelectedTechnician}
                renderOption={(option) => <div className="text-gray-800">{option.name}</div>}
            />
            <SearchableSelect
                label="Select Stock Item"
                placeholder="-- Choose an item --"
                options={stockItems}
                value={selectedStockItem}
                onChange={setSelectedStockItem}
                renderOption={(option) => (
                    <div>
                        <p className="font-medium text-gray-800">{option.name}</p>
                        <p className="text-xs text-gray-500">In Stock: {option.quantityInStock}</p>
                    </div>
                )}
            />
             <SearchableSelect
                label="Select Customer (Optional)"
                placeholder="-- No Customer --"
                options={customers}
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                renderOption={(option) => <div className="text-gray-800">{option.name}</div>}
            />
            <FormInput label="Work Order Number (Optional)" value={workOrderNumber} onChange={(e) => setWorkOrderNumber(e.target.value)} type="text" placeholder="e.g., WO-12345" required={false} />
            <FormTextarea label="Serial Number(s) (Optional)" value={serialNumbers} onChange={(e) => setSerialNumbers(e.target.value)} placeholder="Enter one serial number per line" />
            <FormInput label="Quantity" value={movementQuantity} onChange={(e) => setMovementQuantity(e.target.value)} type="number" placeholder="e.g., 5" min="1" />
            <FormTextarea label="Remarks (Optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Add any notes here" required={false} />
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Photos (Optional)</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                        <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                            <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                                <span>Upload files</span>
                                <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple onChange={handleFileChange} accept="image/*" />
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                </div>
                 {imageFiles.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        {Array.from(imageFiles).map((file, index) => (
                            <img key={index} src={URL.createObjectURL(file)} alt={`preview ${index}`} className="h-24 w-full object-cover rounded-md" />
                        ))}
                    </div>
                )}
                {uploadProgress !== null && (
                    <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                        <p className="text-xs text-center text-gray-600 mt-1">{uploadProgress < 100 ? `Uploading... ${Math.round(uploadProgress)}%` : 'Upload Complete!'}</p>
                    </div>
                )}
            </div>

            <FormInput label="Updated By (Your Name)" value={updatedBy} onChange={(e) => setUpdatedBy(e.target.value)} placeholder="e.g., Jane Smith"/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <PrimaryButton onClick={() => handleMovement('issue')} className="bg-blue-600 hover:bg-blue-700" disabled={uploadProgress !== null && uploadProgress < 100}>Issue to Technician</PrimaryButton>
                <PrimaryButton onClick={() => handleMovement('return')} className="bg-green-600 hover:bg-green-700" disabled={uploadProgress !== null && uploadProgress < 100}>Return to Warehouse</PrimaryButton>
            </div>
        </div>
    );
});

const ActivityLogRow = memo(({ log, categories, onImageClick, showPhotos }) => {
    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'N/A';
    
    const renderIcon = () => {
        const commonClasses = "w-8 h-8 rounded-full flex items-center justify-center";
        switch (log.type) {
            case 'STOCK_ISSUE': return <div className={`${commonClasses} bg-blue-100`}><ArrowLeftRight className="w-4 h-4 text-blue-600" /></div>;
            case 'STOCK_RETURN': return <div className={`${commonClasses} bg-green-100`}><ArrowLeftRight className="w-4 h-4 text-green-600" /></div>;
            case 'STOCK_CREATE': return <div className={`${commonClasses} bg-cyan-100`}><PlusCircle className="w-4 h-4 text-cyan-600" /></div>;
            case 'STOCK_UPDATE': return <div className={`${commonClasses} bg-yellow-100`}><Edit className="w-4 h-4 text-yellow-600" /></div>;
            case 'USER_PASSWORD_RESET': return <div className={`${commonClasses} bg-yellow-100`}><KeyRound className="w-4 h-4 text-yellow-600" /></div>;
            case 'USER_CREATE':
            case 'TECHNICIAN_CREATE':
            case 'CUSTOMER_CREATE':
            case 'CATEGORY_CREATE': return <div className={`${commonClasses} bg-purple-100`}><PlusCircle className="w-4 h-4 text-purple-600" /></div>;
            case 'USER_UPDATE':
            case 'TECHNICIAN_UPDATE':
            case 'CUSTOMER_UPDATE':
            case 'CATEGORY_UPDATE': return <div className={`${commonClasses} bg-orange-100`}><Edit className="w-4 h-4 text-orange-600" /></div>;
            case 'USER_DELETE':
            case 'TECHNICIAN_DELETE':
            case 'CUSTOMER_DELETE':
            case 'CATEGORY_DELETE':
            case 'STOCK_DELETE': return <div className={`${commonClasses} bg-red-100`}><Trash2 className="w-4 h-4 text-red-600" /></div>;
            default: return <div className={`${commonClasses} bg-gray-100`}><FileText className="w-4 h-4 text-gray-600" /></div>;
        }
    };

    const renderDetails = () => {
        const { details, type } = log;
        switch (type) {
            case 'STOCK_ISSUE':
            case 'STOCK_RETURN':
                return (
                    <>
                        <p className="font-semibold text-gray-800 text-sm"><span className={`font-bold ${type === 'STOCK_ISSUE' ? 'text-blue-600' : 'text-green-600'}`}>{details.quantity} x </span> {details.stockItemName} {details.sku && <span className="text-gray-400 font-normal">({details.sku})</span>}</p>
                        <p className="text-xs text-gray-500">{type === 'STOCK_ISSUE' ? 'Issued to' : 'Returned from'} <span className="font-medium">{details.technicianName}</span>{details.customerName && ` for ${details.customerName}`}{details.workOrderNumber && ` (WO: ${details.workOrderNumber})`}</p>
                        {details.quantityAfter !== undefined && <p className="text-xs text-indigo-600 font-medium mt-1">Qty Remaining: {details.quantityAfter}</p>}
                        {details.serialNumbers && <p className="text-xs text-gray-400 mt-1 whitespace-pre-wrap">S/N: {details.serialNumbers}</p>}
                        {details.remarks && <p className="text-xs text-gray-500 mt-1 italic">Remarks: {details.remarks}</p>}
                        {showPhotos && details.imageUrls && details.imageUrls.length > 0 && (
                            <div className="mt-2 flex space-x-2">
                                {details.imageUrls.map((url, index) => (
                                    <button key={index} onClick={() => onImageClick(url)} className="w-12 h-12">
                                        <img src={url} alt={`attachment ${index+1}`} className="w-full h-full object-cover rounded-md" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                );
            case 'STOCK_CREATE':
                return (
                    <>
                        <p className="text-sm text-gray-700">Added new item <span className="font-bold">{details.itemName} ({details.sku})</span> with initial quantity <span className="font-bold">{details.initialQuantity}</span>.</p>
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                             {details.categoryId && <p>Category: <span className="font-medium">{getCategoryName(details.categoryId)}</span></p>}
                             {details.alertThreshold !== undefined && <p>Low Stock Threshold: <span className="font-medium">{details.alertThreshold}</span></p>}
                        </div>
                        {showPhotos && details.imageUrls && details.imageUrls.length > 0 && (
                            <div className="mt-2 flex space-x-2">
                                {details.imageUrls.map((url, index) => (
                                    <button key={index} onClick={() => onImageClick(url)} className="w-12 h-12">
                                        <img src={url} alt={`attachment ${index+1}`} className="w-full h-full object-cover rounded-md" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                );
            case 'STOCK_UPDATE':
                 const changes = Object.entries(details.changes)
                    .filter(([key, value]) => value.from !== value.to)
                    .map(([key, value]) => {
                        let from = value.from;
                        let to = value.to;
                        if (key === 'categoryId') {
                            from = getCategoryName(from);
                            to = getCategoryName(to);
                        }
                        return <li key={key} className="text-xs text-gray-500 list-disc list-inside">Updated {key.replace(/([A-Z])/g, ' $1')}: <span className="font-semibold text-gray-600">"{from}"</span> to <span className="font-semibold text-gray-800">"{to}"</span></li>;
                    });
                return (
                    <>
                        <p className="text-sm text-gray-700">Updated item <span className="font-bold">{details.itemName} {details.sku && <span className="text-gray-400 font-normal">({details.sku})</span>}</span>.</p>
                        {changes.length > 0 ? <ul className="mt-1">{changes}</ul> : <p className="text-xs text-gray-500 italic">No field changes recorded.</p>}
                        {details.quantityAfter !== undefined && <p className="text-xs text-indigo-600 font-medium mt-1">Qty Remaining: {details.quantityAfter}</p>}
                    </>
                );
            case 'STOCK_DELETE':
                return <p className="text-sm text-gray-700">Deleted item <span className="font-bold">{details.itemName} ({details.sku})</span>.</p>;
            case 'CATEGORY_CREATE':
                return <p className="text-sm text-gray-700">Created category <span className="font-bold">{details.name}</span>.</p>;
            case 'CATEGORY_UPDATE':
                return <p className="text-sm text-gray-700">Updated category <span className="font-bold">{details.oldName}</span> to <span className="font-bold">{details.newName}</span>.</p>;
            case 'CATEGORY_DELETE':
                return <p className="text-sm text-gray-700">Deleted category <span className="font-bold">{details.name}</span>.</p>;
            case 'USER_CREATE':
                return <p className="text-sm text-gray-700">Created new user <span className="font-bold">{details.newUserEmail}</span> with role <span className="font-bold">{details.role}</span>.</p>;
            case 'USER_UPDATE': {
                const changes = [];
                if (details.oldRole !== details.newRole) {
                    changes.push(`role from "${details.oldRole}" to "${details.newRole}"`);
                }
                if (details.newRole === 'normal') {
                    const oldCats = details.oldManagedCategories || [];
                    const newCats = details.managedCategories || [];
                    const added = newCats.filter(c => !oldCats.includes(c)).map(id => `"${getCategoryName(id)}"`).join(', ');
                    const removed = oldCats.filter(c => !newCats.includes(c)).map(id => `"${getCategoryName(id)}"`).join(', ');
                    if(added) changes.push(`added categories: ${added}`);
                    if(removed) changes.push(`removed categories: ${removed}`);
                }
                return <p className="text-sm text-gray-700">Updated user <span className="font-bold">{details.targetUserEmail}</span>: {changes.join('; ')}.</p>;
            }
            case 'USER_DELETE':
                return <p className="text-sm text-gray-700">Removed access for user <span className="font-bold">{details.deletedUserEmail}</span>.</p>;
            case 'USER_PASSWORD_RESET':
                return <p className="text-sm text-gray-700">Initiated password reset for user <span className="font-bold">{details.targetUserEmail}</span>.</p>;
            case 'TECHNICIAN_CREATE':
            case 'CUSTOMER_CREATE':
                return <p className="text-sm text-gray-700">Added new {type.split('_')[0].toLowerCase()} <span className="font-bold">{details.name}</span>.</p>;
            case 'TECHNICIAN_UPDATE':
            case 'CUSTOMER_UPDATE':
                return <p className="text-sm text-gray-700">Updated {type.split('_')[0].toLowerCase()} from <span className="font-bold">{details.oldName}</span> to <span className="font-bold">{details.newName}</span>.</p>;
            case 'TECHNICIAN_DELETE':
            case 'CUSTOMER_DELETE':
                return <p className="text-sm text-gray-700">Deleted {type.split('_')[0].toLowerCase()} <span className="font-bold">{details.name}</span>.</p>;
            default:
                return <p className="text-sm text-gray-700">System action: <span className="font-mono text-xs">{type}</span></p>;
        }
    };

    return (
        <li className="p-3">
            <div className="flex justify-between items-start">
                <div className="flex items-center space-x-3">
                    {renderIcon()}
                    <div>{renderDetails()}</div>
                </div>
                <p className="text-xs text-gray-500 text-right shrink-0 ml-2">{log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString() : 'Just now'}</p>
            </div>
            <p className="text-xs text-gray-400 mt-1 pl-11">By: {log.userEmail || 'N/A'}</p>
        </li>
    );
});


const HistoryManager = memo(forwardRef(({ logs, categories, stockItems, showFilters, showGeminiInterface, flashMessage, setError, onImageClick }, ref) => {
    const [mainFilter, setMainFilter] = useState("all");
    const [subFilter, setSubFilter] = useState("all");
    const [dateFilters, setDateFilters] = useState({ startDate: "", endDate: "" });
    const [selectedStockItemFilter, setSelectedStockItemFilter] = useState("");
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [geminiReport, setGeminiReport] = useState("");
    const [showPhotos, setShowPhotos] = useState(false);

    const getFormattedLogDetails = useCallback((log) => {
        const { details, type } = log;
        const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'N/A';

        switch (type) {
            case 'STOCK_ISSUE':
            case 'STOCK_RETURN':
                let issueReturnStr = `${details.quantity} x ${details.stockItemName}${details.sku ? ` (${details.sku})` : ''}. `;
                issueReturnStr += `${type === 'STOCK_ISSUE' ? 'Issued to' : 'Returned from'} ${details.technicianName}`;
                if (details.customerName) issueReturnStr += ` for ${details.customerName}`;
                if (details.workOrderNumber) issueReturnStr += ` (WO: ${details.workOrderNumber})`;
                if (details.quantityAfter !== undefined) issueReturnStr += ` | Qty Remaining: ${details.quantityAfter}`;
                if (details.serialNumbers) issueReturnStr += ` | S/N: ${details.serialNumbers.replace(/\n/g, ', ')}`;
                if (details.remarks) issueReturnStr += ` | Remarks: ${details.remarks}`;
                return issueReturnStr;
            case 'STOCK_CREATE':
                let createStr = `Added: ${details.itemName} (${details.sku}) | Initial Qty: ${details.initialQuantity}`;
                if (details.categoryId) createStr += ` | Category: ${getCategoryName(details.categoryId)}`;
                if (details.alertThreshold !== undefined) createStr += ` | Low Stock Threshold: ${details.alertThreshold}`;
                return createStr;
            case 'STOCK_UPDATE':
                const changes = Object.entries(details.changes)
                    .filter(([key, value]) => value.from !== value.to)
                    .map(([key, value]) => {
                        let from = value.from;
                        let to = value.to;
                        if (key === 'categoryId') {
                            from = getCategoryName(from);
                            to = getCategoryName(to);
                        }
                        return `${key}: "${from}" to "${to}"`;
                    }).join('; ');
                return `Updated item ${details.itemName}${details.sku ? ` (${details.sku})` : ''}. Changes: ${changes || 'none'}. Qty Remaining: ${details.quantityAfter}`;
            case 'STOCK_DELETE':
                return `Deleted item ${details.itemName} (${details.sku}).`;
            case 'CATEGORY_CREATE':
                return `Created category: ${details.name}.`;
            case 'CATEGORY_UPDATE':
                return `Updated category from "${details.oldName}" to "${details.newName}".`;
            case 'CATEGORY_DELETE':
                return `Deleted category: ${details.name}.`;
            case 'USER_CREATE':
                return `Created new user: ${details.newUserEmail} with role: ${details.role}.`;
            case 'USER_UPDATE': {
                const userChanges = [];
                if (details.oldRole !== details.newRole) {
                    userChanges.push(`role from "${details.oldRole}" to "${details.newRole}"`);
                }
                if (details.newRole === 'normal') {
                    const oldCats = details.oldManagedCategories || [];
                    const newCats = details.managedCategories || [];
                    const added = newCats.filter(c => !oldCats.includes(c)).map(id => `"${getCategoryName(id)}"`).join(', ');
                    const removed = oldCats.filter(c => !newCats.includes(c)).map(id => `"${getCategoryName(id)}"`).join(', ');
                    if(added) userChanges.push(`added categories: ${added}`);
                    if(removed) userChanges.push(`removed categories: ${removed}`);
                }
                return `Updated user ${details.targetUserEmail}: ${userChanges.join('; ')}.`;
            }
            case 'USER_DELETE':
                return `Removed access for user: ${details.deletedUserEmail}.`;
            case 'USER_PASSWORD_RESET':
                return `Initiated password reset for user: ${details.targetUserEmail}.`;
            case 'TECHNICIAN_CREATE':
            case 'CUSTOMER_CREATE':
                return `Added new ${type.split('_')[0].toLowerCase()}: ${details.name}.`;
            case 'TECHNICIAN_UPDATE':
            case 'CUSTOMER_UPDATE':
                return `Updated ${type.split('_')[0].toLowerCase()} from "${details.oldName}" to "${details.newName}".`;
            case 'TECHNICIAN_DELETE':
            case 'CUSTOMER_DELETE':
                return `Deleted ${type.split('_')[0].toLowerCase()}: ${details.name}.`;
            default:
                return `System action: ${type}`;
        }
    }, [categories]);

    const handleExportExcel = () => {
        if (filteredLogs.length === 0) {
            flashMessage(setError, "No data to export.");
            return;
        }

        if (!window.XLSX) {
            flashMessage(setError, "Excel export library not ready. Please try again.");
            console.error("XLSX not found on window object.");
            return;
        }

        const dataToExport = filteredLogs.map(log => ({
            "Date": log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString() : 'N/A',
            "User": log.userEmail || 'N/A',
            "Activity Type": log.type,
            "Details": getFormattedLogDetails(log)
        }));
        const ws = window.XLSX.utils.json_to_sheet(dataToExport);
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, "ActivityLog");
        window.XLSX.writeFile(wb, `activity_log_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    useImperativeHandle(ref, () => ({
        handleExportExcel
    }));

    const filterOptions = useMemo(() => ({
        "all": [],
        "stock_movement": ["all", "issue", "return"],
        "inventory_management": ["all", "create", "update", "delete"],
        "user_management": ["all", "create", "update", "delete"],
        "category_management": ["all", "create", "update", "delete"],
        "technician_management": ["all", "create", "update", "delete"],
        "customer_management": ["all", "create", "update", "delete"],
    }), []);

    const handleMainFilterChange = (e) => {
        setMainFilter(e.target.value);
        setSubFilter("all"); // Reset sub-filter when main filter changes
    };

    const handleDateChange = (e) => setDateFilters({ ...dateFilters, [e.target.name]: e.target.value });
    
    const resetFilters = () => {
        setMainFilter("all");
        setSubFilter("all");
        setDateFilters({ startDate: "", endDate: "" });
        setSelectedStockItemFilter("");
    };

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const logDate = log.timestamp?.seconds ? new Date(log.timestamp.seconds * 1000) : new Date();
            const startDate = dateFilters.startDate ? new Date(dateFilters.startDate) : null;
            const endDate = dateFilters.endDate ? new Date(dateFilters.endDate) : null;
            if (startDate) startDate.setHours(0, 0, 0, 0);
            if (endDate) endDate.setHours(23, 59, 59, 999);
            
            const isAfterStartDate = !startDate || logDate >= startDate;
            const isBeforeEndDate = !endDate || logDate <= endDate;

            if (!isAfterStartDate || !isBeforeEndDate) return false;

            const matchesStockItem = !selectedStockItemFilter || (log.details && log.details.stockItemId === selectedStockItemFilter);
            if (!matchesStockItem) return false;

            if (mainFilter === "all") return true;

            const [main, action] = log.type.split('_');
            const logAction = action.toLowerCase();

            let categoryMatch = false;
            if (mainFilter === "stock_movement" && (log.type === 'STOCK_ISSUE' || log.type === 'STOCK_RETURN')) {
                categoryMatch = true;
            } else if (mainFilter === "inventory_management" && main === 'STOCK' && (logAction === 'create' || logAction === 'update' || logAction === 'delete')) {
                categoryMatch = true;
            } else if (mainFilter.startsWith(main.toLowerCase())) {
                categoryMatch = true;
            }
            
            if (!categoryMatch) return false;

            if (subFilter === "all") return true;
            
            if (mainFilter === "stock_movement") {
                 return logAction === subFilter;
            }

            return logAction === subFilter;
        });
    }, [logs, mainFilter, subFilter, dateFilters, selectedStockItemFilter]);

    const handleGenerateReport = async () => {
        if (filteredLogs.length === 0) {
            flashMessage(setError, "No data for the current filter to generate a report.");
            return;
        }
        setIsGeneratingReport(true);
        setGeminiReport("");
        
        const formattedData = filteredLogs.map(log => {
            const { type, details, userEmail, timestamp } = log;
            return {
                date: new Date(timestamp.seconds * 1000).toISOString(),
                user: userEmail,
                action: type,
                details: details
            };
        });

        const workerUrl = "https://gemini-proxy-worker.khine.workers.dev";

        try {
            const response = await fetch(workerUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ formattedData }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Worker call failed with status: ${response.status}. Message: ${errorText}`);
            }
            
            const result = await response.json();
            const reportText = result.report;

            if (reportText) {
                setGeminiReport(reportText);
            } else {
                throw new Error("No content received from AI function.");
            }

        } catch (err) {
            console.error("Gemini report generation failed:", err);
            flashMessage(setError, "Failed to generate AI report. Please check the Worker URL and function logs.");
        } finally {
            setIsGeneratingReport(false);
        }
    };
    
    return (
        <div className="flex flex-col h-full">
            {showFilters && (
                <div className="p-4 bg-gray-50 rounded-lg mb-4 border animate-fade-in-scale">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput label="Start Date" name="startDate" type="date" value={dateFilters.startDate} onChange={handleDateChange} required={false} />
                        <FormInput label="End Date" name="endDate" type="date" value={dateFilters.endDate} onChange={handleDateChange} required={false} />
                        
                        <div className="md:col-span-2">
                            <SearchableSelect
                                label="Filter by Stock Item"
                                placeholder="All Items"
                                options={stockItems}
                                value={selectedStockItemFilter}
                                onChange={setSelectedStockItemFilter}
                                renderOption={(option) => (
                                    <div>
                                        <p className="font-medium text-gray-800">{option.name}</p>
                                        <p className="text-xs text-gray-500">{option.sku}</p>
                                    </div>
                                )}
                            />
                        </div>
                        
                        <FormSelect label="Main Category" name="mainFilter" value={mainFilter} onChange={handleMainFilterChange} required={false}>
                            <option value="all">All Activities</option>
                            <option value="stock_movement">Stock Movement</option>
                            <option value="inventory_management">Inventory Management</option>
                            <option value="user_management">User Management</option>
                            <option value="category_management">Category Management</option>
                            <option value="technician_management">Technician Management</option>
                            <option value="customer_management">Customer Management</option>
                        </FormSelect>

                        {mainFilter !== 'all' && (
                             <FormSelect label="Action Type" name="subFilter" value={subFilter} onChange={(e) => setSubFilter(e.target.value)} required={false}>
                                {filterOptions[mainFilter].map(opt => (
                                    <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                                ))}
                            </FormSelect>
                        )}
                        
                        <div className="md:col-span-2 flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">Activity Photos</label>
                                <ToggleSwitch isEnabled={showPhotos} onToggle={() => setShowPhotos(p => !p)} />
                            </div>
                            <button onClick={resetFilters} className="flex items-center justify-center space-x-2 text-sm text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-4 py-2 rounded-md"><XCircle className="w-4 h-4" /><span>Reset Filters</span></button>
                        </div>
                    </div>
                </div>
            )}
             {showGeminiInterface && (
                <div className="p-4 bg-indigo-50 rounded-lg mb-4 border-2 border-dashed border-indigo-200 animate-fade-in-scale">
                    <PrimaryButton onClick={handleGenerateReport} disabled={isGeneratingReport || filteredLogs.length === 0} className="bg-purple-600 hover:bg-purple-700">
                        <Sparkles className="w-5 h-5"/> <span>{isGeneratingReport ? "Analyzing..." : "✨ Generate Smart Report"}</span>
                    </PrimaryButton>
                </div>
            )}

            {(isGeneratingReport || geminiReport) && (
                <div className="mb-4 p-4 border border-indigo-200 bg-white rounded-lg shadow-sm animate-fade-in-scale">
                    <h3 className="font-bold text-indigo-800 flex items-center space-x-2"><BrainCircuit className="w-5 h-5"/><span>AI Generated Summary</span></h3>
                    {isGeneratingReport && <p className="text-indigo-700 italic mt-2">Generating your report, please wait...</p>}
                    {geminiReport && <p className="text-indigo-900 mt-2 text-sm">{geminiReport}</p>}
                </div>
            )}

            <div className="overflow-y-auto border rounded-md" style={{height: 'calc(100vh - 450px)'}}>
                <ul className="divide-y divide-gray-200">
                    {filteredLogs.map(log => <ActivityLogRow key={log.id} log={log} categories={categories} onImageClick={onImageClick} showPhotos={showPhotos} />)}
                    {filteredLogs.length === 0 && <li className="p-4 text-center text-gray-500">No activity records found.</li>}
                </ul>
            </div>
        </div>
    );
}));

const ToggleSwitch = ({ isEnabled, onToggle }) => (
    <button
        onClick={onToggle}
        className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
            isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
        }`}
    >
        <span
            className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
        />
    </button>
);


const InventoryList = memo(({ stockItems, isAdmin, onEdit, onDelete, favoriteItems, onToggleFavorite }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isManaging, setIsManaging] = useState(false);

    const filteredItems = useMemo(() => {
        if (!searchTerm) return stockItems;
        const lowercasedFilter = searchTerm.toLowerCase();
        return stockItems.filter(item =>
            item.name.toLowerCase().includes(lowercasedFilter) ||
            item.sku.toLowerCase().includes(lowercasedFilter)
        );
    }, [stockItems, searchTerm]);

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 gap-4">
                 <div className="relative flex-grow">
                     <label htmlFor="inventory-search" className="sr-only">Search Inventory</label>
                     <input
                        id="inventory-search"
                        type="text"
                        placeholder="Search by name or SKU..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md"
                     />
                     <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                {isAdmin && (
                    <button
                        onClick={() => setIsManaging(prev => !prev)}
                        className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors w-full sm:w-auto ${
                            isManaging ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'
                        }`}
                    >
                        {isManaging ? 'Finish Managing' : 'Manage Inventory'}
                    </button>
                )}
            </div>
            <div className="max-h-[60vh] overflow-y-auto border rounded-md">
                <ul className="divide-y divide-gray-200">
                    {filteredItems.map(item => (
                        <li key={item.id} className="p-3 flex justify-between items-center text-sm group">
                            <div className="flex-grow pr-4">
                                <p className="font-medium text-gray-800">{item.name}</p>
                                <p className="text-gray-500 text-xs">SKU: {item.sku}</p>
                            </div>
                            <div className="flex items-center space-x-2 shrink-0">
                                <button onClick={() => onToggleFavorite(item.id)} className="p-1.5 text-gray-400 hover:text-red-500">
                                    <Heart className={`w-5 h-5 transition-colors ${favoriteItems.includes(item.id) ? 'text-red-500 fill-current' : ''}`} />
                                </button>
                                <span className={`font-bold px-2 py-1 rounded-full text-xs min-w-[70px] text-center ${item.quantityInStock <= (item.alertThreshold ?? 10) ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-600'}`}>
                                    Qty: {item.quantityInStock}
                                </span>
                                {isAdmin && isManaging && (
                                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => onEdit(item)} className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-100"><Edit size={14} /></button>
                                        <button onClick={() => onDelete(item.id)} className="p-1.5 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-100"><Trash2 size={14} /></button>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                    {filteredItems.length === 0 && <li className="p-4 text-center text-gray-500">No items found.</li>}
                </ul>
            </div>
        </div>
    )
})


// --- AUTHENTICATION COMPONENTS ---
const AuthFormCard = ({ title, children }) => (
    <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-3xl font-bold text-center text-gray-800">{title}</h2>
        {children}
    </div>
);

const LoginForm = ({ auth }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            if (err.code === 'auth/invalid-credential') {
                 setError("Invalid credentials. Please check your email and password. If this persists, your account may not be activated.");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
            console.error("Sign in error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthFormCard title="Welcome Back">
            <form onSubmit={handleSignIn} className="space-y-4">
                {error && <p className="text-red-500 text-sm text-center bg-red-50 p-2 rounded-md">{error}</p>}
                <FormInput label="Email Address" name="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
                <FormInput label="Password" name="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
                <PrimaryButton type="submit" disabled={isLoading} className="bg-indigo-600 hover:bg-indigo-700">{isLoading ? 'Signing In...' : 'Sign In'}</PrimaryButton>
            </form>
        </AuthFormCard>
    );
};

const AuthPage = ({ auth }) => {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
             <div className="flex items-center space-x-3 mb-6">
                <Warehouse className="w-10 h-10 text-indigo-600" />
                <h1 className="text-3xl font-bold text-gray-800">Trinet Inventory</h1>
            </div>
           <LoginForm auth={auth} />
        </div>
    );
};

const LoginSuccessMessage = ({ onFinish }) => {
    useEffect(() => {
        const timer = setTimeout(onFinish, 1000);
        return () => clearTimeout(timer);
    }, [onFinish]);

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center animate-fade-in-scale">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-800 mt-4">Login Successful</h2>
                <p className="text-gray-600">Redirecting to your dashboard...</p>
            </div>
        </div>
    );
};


// --- MAIN APPLICATION COMPONENT ---
const StockTrackerApp = ({ user, auth, db, storage }) => {
    const [stockItems, setStockItems] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [authorizedUsers, setAuthorizedUsers] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmState, setConfirmState] = useState(null);
    const [categoryPath, setCategoryPath] = useState([]);
    const [isSeeding, setIsSeeding] = useState(false);
    const [userRole, setUserRole] = useState('basic');
    const [userPermissions, setUserPermissions] = useState({ managedCategories: [], favoriteItems: [] });
    const [editingStockItem, setEditingStockItem] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [showHistoryFilters, setShowHistoryFilters] = useState(false);
    const [showGeminiInterface, setShowGeminiInterface] = useState(false);
    const [editingSimpleItem, setEditingSimpleItem] = useState(null);
    const [activeTab, setActiveTab] = useState('inventory');
    const [adminSubTab, setAdminSubTab] = useState('users');
    const [viewingImage, setViewingImage] = useState(null);
    const [showHotDashboard, setShowHotDashboard] = useState(false);
    const historyManagerRef = useRef(null);
    
    const flashMessage = useCallback((setter, message) => { 
        setter(message); 
        setTimeout(() => setter(''), 4000); 
    }, []);

    const logActivity = useCallback(async (type, details) => {
        try {
            await addDoc(collection(db, `artifacts/${appId}/public/data/activityLogs`), {
                type,
                details,
                userEmail: user.email,
                timestamp: serverTimestamp()
            });
        } catch (err) {
            console.error("Failed to log activity:", err);
            throw new Error("Failed to write to activity log. You may not have permission.");
        }
    }, [db, user.email]);

    useEffect(() => {
        if (!user) return;
        const userDocRef = doc(db, "authorized_users", user.email);
        const unsubscribe = onSnapshot(userDocRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setUserRole(data.role || 'basic');
                setUserPermissions({ 
                    managedCategories: data.managedCategories || [], 
                    favoriteItems: data.favoriteItems || [] 
                });
            } else {
                setUserRole('basic');
                setUserPermissions({ managedCategories: [], favoriteItems: [] });
                console.warn(`User ${user.email} not found in authorized_users. Defaulting to 'basic' role.`);
            }
        }, (error) => {
             console.error("Permission error checking user role:", error);
             setUserRole('basic');
             setUserPermissions({ managedCategories: [], favoriteItems: [] });
        });
        return () => unsubscribe();
    }, [user, db]);


    useEffect(() => {
        if(userRole === 'basic' && (activeTab !== 'inventory')){
            setActiveTab('inventory');
        }
    }, [userRole, activeTab]);


    useEffect(() => {
        const publicDataPath = `artifacts/${appId}/public/data`;
        
        const createSubscription = (coll, setState, orderField = 'name') => {
            const q = query(collection(db, `${publicDataPath}/${coll}`), orderBy(orderField, orderField === 'timestamp' ? 'desc' : 'asc'));
            return onSnapshot(q, 
                (snap) => setState(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
                (err) => { console.error(`Error fetching ${coll}:`, err); setError(`Could not fetch ${coll}. Check permissions.`); }
            );
        };

        const subs = [
            createSubscription('stockItems', setStockItems),
            createSubscription('technicians', setTechnicians),
            createSubscription('customers', setCustomers),
            createSubscription('productCategories', setCategories),
            createSubscription('activityLogs', setActivityLogs, 'timestamp')
        ];
        
        if (userRole === 'admin') {
            const authUsersQuery = query(collection(db, `authorized_users`));
            const authUsersUnsub = onSnapshot(authUsersQuery, 
                (snap) => setAuthorizedUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
                (err) => { console.error(`Error fetching authorized users:`, err); setError(`Could not fetch user list. Check Firestore rules.`); }
            );
            subs.push(authUsersUnsub);
        }

        return () => subs.forEach(unsub => unsub());
    }, [db, userRole, setError]);
    
    const categoryTree = useMemo(() => {
        const tree = [];
        const map = {};
        categories.forEach(cat => { map[cat.id] = { ...cat, children: [] }; });
        categories.forEach(cat => {
            if (cat.parentId && map[cat.parentId]) map[cat.parentId].children.push(map[cat.id]);
            else tree.push(map[cat.id]);
        });
        return tree;
    }, [categories]);

    // FIX: Memoized map for categories to improve performance of path lookups.
    const categoriesMap = useMemo(() => {
        const map = new Map();
        for (const cat of categories) {
            map.set(cat.id, cat);
        }
        return map;
    }, [categories]);

    // --- Handler Functions ---
    const handleSignOut = () => signOut(auth);
    
    const handleAddCategory = useCallback(async (name, parent, remarks) => {
        if(!name) { flashMessage(setError, "Category name cannot be empty."); return; }
        const data = { name, parentId: parent === 'root' ? null : parent, remarks, createdAt: serverTimestamp() };
        try {
            await addDoc(collection(db, `artifacts/${appId}/public/data/productCategories`), data);
            await logActivity('CATEGORY_CREATE', { name, parentId: data.parentId, remarks });
            flashMessage(setSuccess, "Category added successfully.");
        } catch(err) { flashMessage(setError, "Failed to add category."); console.error(err); }
    }, [db, flashMessage, logActivity]);
    
    const handleUpdateCategory = useCallback(async (id, newName, parent, remarks) => {
        if(!newName) { flashMessage(setError, "Category name cannot be empty."); return; }
        const categoryRef = doc(db, `artifacts/${appId}/public/data/productCategories`, id);
        const oldDoc = await getDoc(categoryRef);
        const oldName = oldDoc.exists() ? oldDoc.data().name : 'N/A';
        const data = { name: newName, parentId: parent === 'root' ? null : parent, remarks };
        try {
            await updateDoc(categoryRef, data);
            await logActivity('CATEGORY_UPDATE', { oldName, newName, parentId: data.parentId, remarks });
            flashMessage(setSuccess, "Category updated successfully.");
        } catch (err) { flashMessage(setError, "Failed to update category."); console.error(err); }
    }, [db, flashMessage, logActivity]);

    const handleDeleteCategory = useCallback(async (id) => {
        const categoryRef = doc(db, `artifacts/${appId}/public/data/productCategories`, id);
        const docToDelete = await getDoc(categoryRef);
        const categoryData = docToDelete.exists() ? docToDelete.data() : { name: 'N/A' };
        try {
            await deleteDoc(categoryRef);
            await logActivity('CATEGORY_DELETE', { name: categoryData.name });
            flashMessage(setSuccess, "Category deleted successfully.");
        } catch (err) { flashMessage(setError, "Failed to delete category."); console.error(err); }
    }, [db, flashMessage, logActivity]);
    
    const handleDeleteCategoryClick = useCallback((id) => {
        setConfirmState({
            title: "Delete Category",
            message: "Are you sure you want to delete this category? This action cannot be undone.",
            onConfirm: () => {
                handleDeleteCategory(id);
                setConfirmState(null);
            }
        });
    }, [handleDeleteCategory]);

    const handleAddStockItem = useCallback(async (name, sku, quantity, categoryId, alertThreshold, imageFiles, setProgress) => {
        if (!name || !sku || quantity === '' || !categoryId || alertThreshold === '') {
            flashMessage(setError, "Please fill all required fields.");
            return;
        }
        const parsedQuantity = parseInt(quantity, 10);
        const parsedThreshold = parseInt(alertThreshold, 10);

        if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
            flashMessage(setError, "Please enter a valid, positive quantity.");
            return;
        }
         if (isNaN(parsedThreshold) || parsedThreshold < 0) {
            flashMessage(setError, "Please enter a valid, non-negative alert threshold.");
            return;
        }

        const stockItemsRef = collection(db, `artifacts/${appId}/public/data/stockItems`);
        const q = query(stockItemsRef, where("sku", "==", sku));

        try {
            const querySnapshot = await getDocs(q);
            // FIX: Replaced find() in a loop with a direct lookup on the memoized categoriesMap.
            const getCategoryPath = (catId) => {
                const path = [];
                let current = categoriesMap.get(catId);
                while (current) {
                    path.unshift(current.name);
                    current = categoriesMap.get(current.parentId);
                }
                return path;
            };

            if (!querySnapshot.empty) {
                const existingDoc = querySnapshot.docs[0];
                const stockItemRef = doc(db, `artifacts/${appId}/public/data/stockItems`, existingDoc.id);
                
                let oldQty = 0;
                let newQuantity = 0;
                await runTransaction(db, async (transaction) => {
                    const sfDoc = await transaction.get(stockItemRef);
                    if (!sfDoc.exists()) throw new Error("Document does not exist!");
                    oldQty = sfDoc.data().quantityInStock;
                    newQuantity = oldQty + parsedQuantity;
                    transaction.update(stockItemRef, { quantityInStock: newQuantity });
                });

                await logActivity('STOCK_UPDATE', {
                    itemName: name,
                    sku: existingDoc.data().sku,
                    changes: { quantityInStock: { from: oldQty, to: newQuantity } },
                    quantityAfter: newQuantity
                });
                flashMessage(setSuccess, `Updated quantity for ${name}. Cannot add photos to an existing item from this form.`);
            } else {
                const data = { name, sku, quantityInStock: parsedQuantity, categoryId, alertThreshold: parsedThreshold, categoryPath: getCategoryPath(categoryId), createdAt: serverTimestamp(), imageUrls: [] };
                
                const newDocRef = await addDoc(stockItemsRef, data);
                
                let imageUrls = [];
                if (imageFiles && imageFiles.length > 0) {
                     const uploadPromises = imageFiles.map((file) => {
                        const filePath = `uploads/stockItems/${newDocRef.id}/${file.name}`;
                        const storageRef = ref(storage, filePath);
                        const uploadTask = uploadBytesResumable(storageRef, file);

                        return new Promise((resolve, reject) => {
                            uploadTask.on('state_changed', 
                                (snapshot) => {
                                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                    setProgress(progress);
                                }, 
                                (error) => { console.error("Upload failed:", error); reject(error); }, 
                                async () => {
                                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                    resolve(downloadURL);
                                }
                            );
                        });
                    });
                    imageUrls = await Promise.all(uploadPromises);
                    await updateDoc(newDocRef, { imageUrls });
                }

                await logActivity('STOCK_CREATE', { itemName: name, sku, initialQuantity: parsedQuantity, alertThreshold: parsedThreshold, imageUrls, categoryId: categoryId, quantityAfter: parsedQuantity });
                flashMessage(setSuccess, `Successfully added new item: ${name}.`);
            }
        } catch (err) {
            console.error(err);
            flashMessage(setError, "Failed to add or update stock item.");
        }
    // FROM: }, [db, storage, categories, categoriesMap, flashMessage, logActivity]);
    // TO:
    }, [db, storage, categoriesMap, flashMessage, logActivity]);

    const handleUpdateStockItem = useCallback(async (itemId, updatedData) => {
       const { name, sku, quantityInStock, categoryId, alertThreshold } = updatedData;
       if (!name || !sku || quantityInStock === '' || !categoryId || alertThreshold === '') {
           flashMessage(setError, "Please fill all fields.");
           return;
       }
       const parsedQuantity = parseInt(quantityInStock, 10);
       const parsedThreshold = parseInt(alertThreshold, 10);

       if (isNaN(parsedQuantity) || parsedQuantity < 0) {
           flashMessage(setError, "Please enter a valid, non-negative quantity.");
           return;
       }
        if (isNaN(parsedThreshold) || parsedThreshold < 0) {
            flashMessage(setError, "Please enter a valid, non-negative alert threshold.");
            return;
        }

       // FIX: Replaced find() in a loop with a direct lookup on the memoized categoriesMap.
       const getCategoryPath = (catId) => {
           const path = [];
           let current = categoriesMap.get(catId);
           while(current) {
               path.unshift(current.name);
               current = categoriesMap.get(current.parentId);
           }
           return path;
       };

       const itemRef = doc(db, `artifacts/${appId}/public/data/stockItems`, itemId);
       const oldItemSnap = await getDoc(itemRef);
       const oldData = oldItemSnap.exists() ? oldItemSnap.data() : {};

       const finalData = {
           name,
           sku,
           quantityInStock: parsedQuantity,
           categoryId,
           alertThreshold: parsedThreshold,
           categoryPath: getCategoryPath(categoryId)
       };

       try {
           await updateDoc(itemRef, finalData);
            await logActivity('STOCK_UPDATE', {
                itemName: oldData.name,
                sku: oldData.sku,
                changes: {
                    name: { from: oldData.name, to: finalData.name },
                    sku: { from: oldData.sku, to: finalData.sku },
                    quantityInStock: { from: oldData.quantityInStock, to: finalData.quantityInStock },
                    categoryId: { from: oldData.categoryId, to: finalData.categoryId },
                    alertThreshold: { from: oldData.alertThreshold ?? 10, to: finalData.alertThreshold }
                },
                quantityAfter: finalData.quantityInStock
            });
           flashMessage(setSuccess, `Successfully updated ${name}.`);
           setEditingStockItem(null);
       } catch (err) {
           console.error("Failed to update stock item:", err);
           flashMessage(setError, err.message || "Failed to update stock item.");
       }
    // FROM: }, [db, categories, categoriesMap, flashMessage, logActivity]);
    // TO:
    }, [db, categoriesMap, flashMessage, logActivity]);

    const handleDeleteStockItem = useCallback(async (id) => {
        const hasMovements = activityLogs.some(log => (log.type === 'STOCK_ISSUE' || log.type === 'STOCK_RETURN') && log.details.stockItemId === id);
        if (hasMovements) {
            alert("Cannot delete this item as it has a movement history. You can edit the item and set its quantity to zero instead.");
            return;
        }
        const itemRef = doc(db, `artifacts/${appId}/public/data/stockItems`, id);
        const oldItemSnap = await getDoc(itemRef);
        const oldData = oldItemSnap.exists() ? oldItemSnap.data() : {name: "N/A", sku: "N/A"};
        try {
            await deleteDoc(itemRef);
            await logActivity('STOCK_DELETE', { itemName: oldData.name, sku: oldData.sku });
            flashMessage(setSuccess, "Item deleted successfully.");
        } catch (err) {
            flashMessage(setError, "Failed to delete item.");
            console.error(err);
        }
    }, [db, activityLogs, flashMessage, logActivity]);

    const handleDeleteStockItemClick = useCallback((id) => {
        setConfirmState({
            title: "Delete Stock Item",
            message: "Are you sure you want to permanently delete this item? This action cannot be undone.",
            onConfirm: () => {
                handleDeleteStockItem(id);
                setConfirmState(null);
            }
        });
    }, [handleDeleteStockItem]);


    const handleStockMovement = useCallback(async (type, moveData, setProgress) => {
        const { selectedTechnician, selectedStockItem, movementQuantity, updatedBy, imageFiles } = moveData;
        if (!selectedTechnician || !selectedStockItem || !movementQuantity || !updatedBy) { 
            flashMessage(setError, "Please select a technician, item, quantity, and enter your name."); 
            return false;
        }
        const quantity = parseInt(movementQuantity, 10);
        if (isNaN(quantity) || quantity <= 0) { 
            flashMessage(setError, "Please enter a valid, positive quantity."); 
            return false;
        }
        
        const stockItemRef = doc(db, `artifacts/${appId}/public/data/stockItems`, selectedStockItem);
        
        try {
            let stockItemData = {};
            let newStock = 0;
            await runTransaction(db, async (transaction) => {
                const stockItemDoc = await transaction.get(stockItemRef);
                if (!stockItemDoc.exists()) throw new Error("Stock item not found.");
                stockItemData = stockItemDoc.data();
                const currentStock = stockItemData.quantityInStock;
                if (type === 'issue' && currentStock < quantity) { 
                    throw new Error(`Not enough stock for ${stockItemData.name}. Only ${currentStock} available.`); 
                }
                newStock = type === 'issue' ? currentStock - quantity : currentStock + quantity;
                transaction.update(stockItemRef, { quantityInStock: newStock });
            });

            const transactionId = doc(collection(db, 'dummy')).id; // Generate a unique ID
            let imageUrls = [];

            if (imageFiles && imageFiles.length > 0) {
                const uploadPromises = imageFiles.map((file, index) => {
                    const filePath = `uploads/movements/${transactionId}/${file.name}`;
                    const storageRef = ref(storage, filePath);
                    const uploadTask = uploadBytesResumable(storageRef, file);

                    return new Promise((resolve, reject) => {
                        uploadTask.on('state_changed', 
                            (snapshot) => {
                                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                setProgress(progress);
                            }, 
                            (error) => {
                                console.error("Upload failed:", error);
                                reject(error);
                            }, 
                            async () => {
                                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                resolve(downloadURL);
                            }
                        );
                    });
                });
                imageUrls = await Promise.all(uploadPromises);
            }

            const logDetails = {
                ...moveData,
                imageUrls,
                technicianId: moveData.selectedTechnician,
                technicianName: technicians.find(t => t.id === moveData.selectedTechnician).name,
                stockItemId: moveData.selectedStockItem,
                stockItemName: stockItemData.name,
                sku: stockItemData.sku,
                categoryId: stockItemData.categoryId,
                customerId: moveData.selectedCustomer || null,
                customerName: customers.find(c => c.id === moveData.selectedCustomer)?.name || null,
                quantity,
                quantityAfter: newStock
            };
            delete logDetails.imageFiles;

            await logActivity(type === 'issue' ? 'STOCK_ISSUE' : 'STOCK_RETURN', logDetails);
            
            flashMessage(setSuccess, `Transaction successful.`);
            return true;
        } catch (err) { 
            console.error("Stock movement or logging failed:", err); 
            flashMessage(setError, err.message || "Failed to process stock movement."); 
            return false;
        }
    }, [db, storage, technicians, customers, flashMessage, logActivity]);
    
    const handleAddSimple = useCallback(async (collectionName, name) => {
        if (!name) { flashMessage(setError, `Please enter a name.`); return; }
        const type = `${collectionName.slice(0, -1).toUpperCase()}_CREATE`;
        try {
            await addDoc(collection(db, `artifacts/${appId}/public/data/${collectionName}`), { name, createdAt: serverTimestamp() });
            await logActivity(type, { name });
            flashMessage(setSuccess, `Added successfully.`);
        } catch (err) {
            flashMessage(setError, `Failed to add.`);
        }
    },[db, flashMessage, logActivity]);

     const handleUpdateSimple = useCallback(async (collectionName, id, newName) => {
        if (!newName) { flashMessage(setError, "Name cannot be empty."); return; }
        const type = `${collectionName.slice(0, -1).toUpperCase()}_UPDATE`;
        const docRef = doc(db, `artifacts/${appId}/public/data/${collectionName}`, id);
        const oldDoc = await getDoc(docRef);
        const oldName = oldDoc.exists() ? oldDoc.data().name : 'N/A';
        try {
            await updateDoc(docRef, { name: newName });
            await logActivity(type, { oldName, newName });
            flashMessage(setSuccess, "Updated successfully.");
        } catch (err) {
            flashMessage(setError, "Failed to update.");
            console.error(err);
        }
    }, [db, flashMessage, logActivity]);

    const handleDeleteSimple = useCallback(async (collectionName, id) => {
        const hasMovements = activityLogs.some(log => {
            if (collectionName === 'technicians') return (log.type === 'STOCK_ISSUE' || log.type === 'STOCK_RETURN') && log.details.technicianId === id;
            if (collectionName === 'customers') return (log.type === 'STOCK_ISSUE' || log.type === 'STOCK_RETURN') && log.details.customerId === id;
            return false;
        });

        if (hasMovements) {
             alert(`Cannot delete. This item is still associated with stock movements.`);
             return;
        }
        const type = `${collectionName.slice(0, -1).toUpperCase()}_DELETE`;
        const docRef = doc(db, `artifacts/${appId}/public/data/${collectionName}`, id);
        const oldDoc = await getDoc(docRef);
        const oldName = oldDoc.exists() ? oldDoc.data().name : 'N/A';
        try {
            await deleteDoc(docRef);
            await logActivity(type, { name: oldName });
            flashMessage(setSuccess, "Deleted successfully.");
        } catch (err) {
            flashMessage(setError, "Failed to delete.");
            console.error(err);
        }
    }, [db, activityLogs, flashMessage, logActivity]);

    const handleDeleteSimpleClick = useCallback((collectionName, id) => {
        const title = `Delete ${collectionName.slice(0, -1)}`;
        setConfirmState({
            title: title,
            message: "Are you sure you want to delete this item?",
            onConfirm: () => {
                handleDeleteSimple(collectionName, id);
                setConfirmState(null);
            }
        });
    }, [handleDeleteSimple]);
    
    const handleAddNewUser = useCallback(async (email, password, role) => {
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            return 'Please enter a valid email address.';
        }
        if (password.length < 6) {
            return 'Password must be at least 6 characters long.';
        }

        const tempAppName = `createUser-${Date.now()}`;
        const tempApp = initializeApp(firebaseConfig, tempAppName);
        const tempAuth = getAuth(tempApp);

        try {
            await createUserWithEmailAndPassword(tempAuth, email, password);
            const userDocRef = doc(db, "authorized_users", email);
            await setDoc(userDocRef, { role, managedCategories: [], favoriteItems: [] }); // Add favoriteItems
            await logActivity('USER_CREATE', { newUserEmail: email, role });
            return null; // Return null on success
        } catch (err) {
            console.error('Failed to add new user:', err);
            if (err.code === 'auth/weak-password') {
                return 'Password is too weak. It must be at least 6 characters long.';
            }
            if (err.code === 'auth/email-already-in-use') {
                return 'This email address is already in use.';
            }
            if (err.code === 'auth/password-does-not-meet-requirements') {
                const match = err.message.match(/Firebase: (.*) \(/);
                if (match && match[1]) {
                    return match[1];
                }
                return 'Password does not meet complexity requirements. Please try a stronger password.';
            }
            return 'Failed to create user. You may not have permission.';
        } finally {
            await deleteApp(tempApp);
        }
    }, [db, logActivity]);
    
    const handleSaveUser = useCallback(async (email, newRole, managedCategories) => {
        const userDocRef = doc(db, "authorized_users", email);
        try {
            const oldDoc = await getDoc(userDocRef);
            const oldData = oldDoc.exists() ? oldDoc.data() : {};
            
            const dataToUpdate = { 
                role: newRole,
                managedCategories: newRole === 'normal' ? managedCategories : []
            };

            await updateDoc(userDocRef, dataToUpdate);

            const logDetails = {
                targetUserEmail: email,
                oldRole: oldData.role || 'N/A',
                newRole: newRole,
                oldManagedCategories: oldData.managedCategories || [],
                managedCategories: dataToUpdate.managedCategories
            };

            await logActivity('USER_UPDATE', logDetails);
            flashMessage(setSuccess, `Successfully updated ${email}.`);
        } catch (err) {
            console.error('Failed to update user role:', err);
            flashMessage(setError, 'Failed to update role. You may not have permission.');
        }
    }, [db, flashMessage, logActivity]);

    const handleDeleteUser = useCallback(async (email) => {
        if (email === user.email) {
            alert("You cannot delete your own account.");
            return;
        }
        const userDocRef = doc(db, "authorized_users", email);
        try {
            await deleteDoc(userDocRef);
            await logActivity('USER_DELETE', { deletedUserEmail: email });
            flashMessage(setSuccess, `Successfully removed user ${email}.`);
        } catch (err) {
            console.error('Failed to delete user:', err);
            flashMessage(setError, 'Failed to remove user. You may not have permission.');
        }
    }, [db, flashMessage, logActivity, user.email]);

    const handleDeleteUserClick = useCallback((email) => {
        setConfirmState({
            title: "Remove User",
            message: `Are you sure you want to remove access for ${email}? This will not delete their login account but will remove their role.`,
            onConfirm: () => {
                handleDeleteUser(email);
                setConfirmState(null);
            }
        });
    }, [handleDeleteUser]);

    const handleSendPasswordReset = useCallback(async (email) => {
        if (!email) return false;
        try {
            await sendPasswordResetEmail(auth, email);
            await logActivity('USER_PASSWORD_RESET', { targetUserEmail: email });
            flashMessage(setSuccess, `Password reset email sent successfully to ${email}.`);
            return true; // Indicate success
        } catch (err) {
            console.error("Password reset error:", err);
            flashMessage(setError, "Failed to send password reset email. Check console for details.");
            return false; // Indicate failure
        }
    }, [auth, flashMessage, logActivity]);

    const handleToggleFavorite = useCallback(async (itemId) => {
        const userDocRef = doc(db, "authorized_users", user.email);
        try {
            await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userDocRef);
                if (!userDoc.exists()) {
                    throw new Error("User document does not exist!");
                }
                const currentFavorites = userDoc.data().favoriteItems || [];
                const isFavorited = currentFavorites.includes(itemId);
                const newFavorites = isFavorited
                    ? currentFavorites.filter(id => id !== itemId)
                    : [...currentFavorites, itemId];
                transaction.update(userDocRef, { favoriteItems: newFavorites });
            });
        } catch (err) {
            console.error("Failed to update favorites:", err);
            flashMessage(setError, "Could not update favorites. Check permissions.");
        }
    }, [db, user.email, flashMessage]);

    const seedCategories = useCallback(async () => {
        setIsSeeding(true);
        try {
            const categoriesRef = collection(db, `artifacts/${appId}/public/data/productCategories`);
            const existingCategoriesSnap = await getDocs(categoriesRef);
            if (!existingCategoriesSnap.empty) {
                flashMessage(setSuccess, "Default categories have already been added.");
                setIsSeeding(false);
                return;
            }

            const batch = writeBatch(db);
            const addCatsRecursive = (obj, parentId) => {
                for (const key in obj) {
                    const newDocRef = doc(categoriesRef);
                    batch.set(newDocRef, { name: key, parentId: parentId, remarks: "", createdAt: serverTimestamp() });
                    addCatsRecursive(obj[key], newDocRef.id);
                }
            };
            addCatsRecursive(initialCategoryData, null);
            await batch.commit();
            await logActivity('SYSTEM_EVENT', { description: 'Seeded initial product categories.' });
            flashMessage(setSuccess, "Default categories have been successfully added!");
        } catch (err) { 
            flashMessage(setError, "Failed to seed categories."); 
            console.error(err); 
        } finally { 
            setIsSeeding(false); 
        }
    }, [db, flashMessage, logActivity]);
    
    const { currentDashboardTitle, breadcrumbs, displayedItems, displayedSubCategories } = useMemo(() => {
        const getDescendantIds = (parentId, allCategories) => {
            const children = allCategories.filter(c => c.parentId === parentId);
            let descendantIds = children.map(c => c.id);
            children.forEach(child => {
                descendantIds = [...descendantIds, ...getDescendantIds(child.id, allCategories)];
            });
            return descendantIds;
        };
    
        const countItemsRecursive = (catId, allCategories, allItems) => {
            const descendantIds = getDescendantIds(catId, allCategories);
            const allIds = [catId, ...descendantIds];
            return allItems.filter(item => allIds.includes(item.categoryId)).reduce((sum, item) => sum + item.quantityInStock, 0);
        };

        if (categoryPath.length === 0) {
            const topLevelCats = categories.filter(c => !c.parentId);
            const subs = topLevelCats.map(cat => ({
                ...cat,
                totalQuantity: countItemsRecursive(cat.id, categories, stockItems)
            }));
            return {
                currentDashboardTitle: 'Main Inventory Dashboard',
                breadcrumbs: [],
                displayedItems: [],
                displayedSubCategories: subs,
            };
        }

        const currentCat = categoryPath[categoryPath.length - 1];
        const directChildren = categories.filter(c => c.parentId === currentCat.id);

        let items = [];
        let subs = [];

        if (directChildren.length > 0) {
             subs = directChildren.map(cat => ({
                ...cat,
                totalQuantity: countItemsRecursive(cat.id, categories, stockItems)
            }));
        } else {
             items = stockItems.filter(item => item.categoryId === currentCat.id);
        }

        const breadcrumbs = [{ id: null, name: 'Dashboard' }, ...categoryPath];
        
        return {
            currentDashboardTitle: currentCat.name,
            breadcrumbs,
            displayedItems: items,
            displayedSubCategories: subs,
        };
    }, [categoryPath, categories, stockItems]);
    
    const handleBreadcrumbClick = (index) => {
        setCategoryPath(prev => prev.slice(0, index));
    };


    const lowStockItems = useMemo(() => stockItems.filter(item => item.quantityInStock <= (item.alertThreshold ?? 10)), [stockItems]);
    
    const favoriteStockItems = useMemo(() => {
        const favoriteIds = new Set(userPermissions.favoriteItems);
        return stockItems.filter(item => favoriteIds.has(item.id));
    }, [stockItems, userPermissions.favoriteItems]);

    const isAdmin = userRole === 'admin';
    const isNormal = userRole === 'normal';

    const manageableCategories = useMemo(() => {
        if (isAdmin) {
            return categories;
        }
        if (isNormal && categories.length > 0 && userPermissions && userPermissions.managedCategories) {
            const getDescendantIds = (parentId, allCategories) => {
                const descendants = [];
                const children = allCategories.filter(c => c.parentId === parentId);
                for (const child of children) {
                    descendants.push(child.id);
                    descendants.push(...getDescendantIds(child.id, allCategories));
                }
                return descendants;
            };

            const allowedCategoryIds = new Set(userPermissions.managedCategories);
            userPermissions.managedCategories.forEach(catId => {
                allowedCategoryIds.add(catId);
                const descendantIds = getDescendantIds(catId, categories);
                descendantIds.forEach(descId => allowedCategoryIds.add(descId));
            });

            return categories.filter(cat => allowedCategoryIds.has(cat.id));
        }
        return [];
    // FIX: Removed redundant 'userRole' from dependency array.
    }, [categories, isAdmin, isNormal, userPermissions]);

    const TabButton = ({ tabName, label, icon }) => {
        const Icon = icon;
        const isActive = activeTab === tabName;
        return (
            <button
                onClick={() => setActiveTab(tabName)}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                    isActive ? 'border-indigo-500 text-indigo-600 bg-indigo-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
                <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                <span className="hidden sm:inline">{label}</span>
            </button>
        )
    };

    const AdminSubTabButton = ({ tabName, label, icon }) => {
        const Icon = icon;
        const isActive = adminSubTab === tabName;
        return (
             <button
                onClick={() => setAdminSubTab(tabName)}
                className={`flex items-center space-x-2 w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
            >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
            </button>
        )
    }

    return (
        <div className="bg-gray-100 min-h-screen font-sans">
            <header className="bg-white shadow-sm sticky top-0 z-10">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <Warehouse className="w-8 h-8 text-indigo-600" />
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Trinet Inventory</h1>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-4">
                       <div className="text-right">
                            <span className="text-sm text-gray-800 font-medium block truncate max-w-[150px] sm:max-w-none">{user.email}</span>
                            {isAdmin && (
                                <div className="flex items-center justify-end space-x-1.5 text-xs text-green-600 font-semibold">
                                    <ShieldCheck size={13} />
                                    <span>Admin User</span>
                                </div>
                            )}
                             {isNormal && (
                                <div className="flex items-center justify-end space-x-1.5 text-xs text-gray-500 font-medium">
                                    <User size={13} />
                                    <span>Normal User</span>
                                </div>
                            )}
                             {userRole === 'basic' && (
                                <div className="flex items-center justify-end space-x-1.5 text-xs text-blue-500 font-medium">
                                    <User size={13} />
                                    <span>Basic User</span>
                                </div>
                            )}
                        </div>
                        <button onClick={handleSignOut} className="flex items-center space-x-2 text-sm text-red-500 hover:text-red-700 font-medium p-2 rounded-md hover:bg-red-50">
                            <LogOut className="w-5 h-5 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Sign Out</span>
                        </button>
                    </div>
                </div>
            </header>
            <main className="container mx-auto p-4 md:p-6">
                {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-md shadow" role="alert">{error}</div>}
                {success && <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded-md shadow" role="alert">{success}</div>}
                
                <div className="mb-6 bg-white p-4 rounded-xl shadow-md">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-3 mb-4">
                        <div className="flex items-center space-x-3 self-start sm:self-center">
                            <LayoutDashboard className="w-6 h-6 text-indigo-500"/>
                             <h2 className="text-lg font-bold text-gray-800">{showHotDashboard ? 'Favourite Inventory Dashboard' : currentDashboardTitle}</h2>
                        </div>
                        <div className="flex items-center space-x-2 self-end sm:self-center">
                             <span className={`text-sm font-medium ${!showHotDashboard ? 'text-indigo-600' : 'text-gray-500'}`}>Default</span>
                            <ToggleSwitch isEnabled={showHotDashboard} onToggle={() => { setShowHotDashboard(prev => !prev); setCategoryPath([]); }} />
                            <span className={`text-sm font-medium ${showHotDashboard ? 'text-indigo-600' : 'text-gray-500'}`}>Favourite</span>
                        </div>
                    </div>
                    
                    {showHotDashboard ? (
                        <div>
                             {favoriteStockItems.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-fade-in-scale">
                                    {favoriteStockItems.map(item => (
                                         <div key={item.id} className="bg-white p-4 rounded-lg shadow-md flex flex-col justify-between border transition-all duration-200 hover:bg-indigo-600 hover:text-white group cursor-pointer hover:shadow-xl hover:border-indigo-400">
                                             <div>
                                                 <p className="font-bold text-gray-800 group-hover:text-white">{item.name}</p>
                                                 <p className="text-xs text-gray-500 mt-1 group-hover:text-indigo-100">SKU: {item.sku}</p>
                                             </div>
                                             <div className="mt-4 text-right">
                                                  <span className="font-bold text-3xl text-indigo-600 group-hover:text-white">{item.quantityInStock}</span>
                                                  <span className="text-sm text-gray-500 ml-1 group-hover:text-indigo-100">units</span>
                                             </div>
                                         </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Heart className="w-12 h-12 mx-auto text-gray-300" />
                                    <p className="mt-2 text-gray-500">You haven't favorited any items yet.</p>
                                    <p className="text-xs text-gray-400">Click the heart icon on items in the 'Current Inventory' tab.</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                             {breadcrumbs.length > 0 && (
                                <div className="mb-4 -mx-4 px-4 overflow-x-auto no-scrollbar">
                                   <div className="text-sm text-gray-500 flex items-center space-x-2 whitespace-nowrap">
                                       {breadcrumbs.map((crumb, index) => (
                                           <React.Fragment key={crumb.id || 'root'}>
                                               <button onClick={() => handleBreadcrumbClick(index)} className="hover:underline shrink-0">
                                                   {crumb.name}
                                               </button>
                                               {index < breadcrumbs.length - 1 && <span className="text-gray-300">/</span>}
                                           </React.Fragment>
                                       ))}
                                   </div>
                                </div>
                            )}
                             <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 animate-fade-in-scale">
                                {displayedSubCategories.map(cat => (
                                    <button key={cat.id} onClick={() => setCategoryPath(prev => [...prev, {id: cat.id, name: cat.name}])} className={`p-4 rounded-lg flex flex-col items-center justify-center text-center transition-all duration-200 bg-gray-50 hover:bg-indigo-600 hover:text-white hover:shadow-lg hover:-translate-y-1 active:scale-95 active:bg-indigo-700 group`}>
                                        <p className="text-2xl font-bold text-indigo-600 group-hover:text-white">{cat.totalQuantity}</p>
                                        <p className="text-sm font-medium text-gray-700 group-hover:text-white">{cat.name}</p>
                                    </button>
                                  ))}
                             </div>
                             {displayedItems.length > 0 && (
                                <div className="animate-fade-in-scale space-y-6">
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {displayedItems.map(item => (
                                            <div key={item.id} className="bg-white p-4 rounded-lg shadow-md flex flex-col justify-between border transition-all duration-200 hover:bg-indigo-600 hover:text-white group cursor-pointer hover:shadow-xl hover:border-indigo-400">
                                                <div>
                                                    <p className="font-bold text-gray-800 group-hover:text-white">{item.name}</p>
                                                    <p className="text-xs text-gray-500 mt-1 group-hover:text-indigo-100">SKU: {item.sku}</p>
                                                </div>
                                                <div className="mt-4 text-right">
                                                     <span className="font-bold text-3xl text-indigo-600 group-hover:text-white">{item.quantityInStock}</span>
                                                     <span className="text-sm text-gray-500 ml-1 group-hover:text-indigo-100">units</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                             )}
                             {categoryPath.length > 0 && displayedSubCategories.length === 0 && displayedItems.length === 0 && (
                                <p className="text-center text-gray-500 py-8">No items or sub-categories found.</p>
                             )}
                        </>
                    )}
              </div>
              
                {lowStockItems.length > 0 && (
                    <div className="mb-6 bg-red-50 border border-red-200 p-4 rounded-xl shadow-md">
                        <div className="flex items-center space-x-3 mb-3">
                            <AlertTriangle className="w-6 h-6 text-red-500"/>
                            <h2 className="text-lg font-bold text-red-800">Low Stock Alert</h2>
                        </div>
                        <ul className="divide-y divide-red-200">
                            {lowStockItems.map(item => (
                                <li key={item.id} className="py-2 flex justify-between items-center text-sm">
                                    <span className="font-medium text-red-700">{item.name} ({item.sku})</span>
                                    <span className="font-bold text-red-600 bg-red-100 px-2 py-1 rounded-full">{item.quantityInStock} left (Threshold: {item.alertThreshold ?? 10})</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {/* --- New Tabbed Layout --- */}
                <div className="bg-white rounded-xl shadow-md">
                    <div className="px-4 border-b border-gray-200">
                         <nav className="-mb-px flex space-x-2 sm:space-x-4 flex-wrap" aria-label="Tabs">
                            <TabButton tabName="inventory" label="Current Inventory" icon={Package} />
                            {(isNormal || isAdmin) && <TabButton tabName="movement" label="Stock Movement" icon={ArrowLeftRight} />}
                             {(isNormal || isAdmin) && <TabButton tabName="add_stock" label="Add Stock" icon={PackagePlus} />}
                            {(isNormal || isAdmin) && <TabButton tabName="log" label="Activity Log" icon={History} />}
                            {isAdmin && <TabButton tabName="admin" label="Admin Panel" icon={Settings} />}
                         </nav>
                    </div>

                    <div className="p-4 md:p-6">
                         {activeTab === 'inventory' && (
                            <InventoryList 
                                stockItems={stockItems} 
                                isAdmin={isAdmin} 
                                onEdit={setEditingStockItem} 
                                onDelete={handleDeleteStockItemClick}
                                favoriteItems={userPermissions.favoriteItems}
                                onToggleFavorite={handleToggleFavorite}
                            />
                        )}
                        {activeTab === 'movement' && (isNormal || isAdmin) && (
                            <StockMovementManager technicians={technicians} stockItems={stockItems} customers={customers} onMove={handleStockMovement} />
                        )}
                        {activeTab === 'add_stock' && (isNormal || isAdmin) && (
                            <StockItemManager categories={manageableCategories} onAdd={handleAddStockItem} userPermissions={{role: userRole, managedCategories: userPermissions.managedCategories }}/>
                        )}
                        {activeTab === 'log' && (isNormal || isAdmin) && (
                             <>
                                <div className="flex justify-end items-center mb-4 space-x-2">
                                    <button onClick={() => historyManagerRef.current?.handleExportExcel()} className="flex items-center space-x-2 p-2 rounded-md hover:bg-green-50 text-gray-500 hover:text-green-700 text-sm font-medium" title="Export as Excel">
                                        <FileSpreadsheet className="w-5 h-5"/>
                                        <span>Excel</span>
                                    </button>
                                    <button 
                                        onClick={() => setShowGeminiInterface(prev => !prev)} 
                                        className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                                        title="Generate Smart Report" >
                                        <Sparkles className="w-5 h-5"/>
                                    </button>
                                    <button 
                                        onClick={() => setShowHistoryFilters(prev => !prev)} 
                                        className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                                        title="Filter Logs" >
                                        <Filter className="w-5 h-5"/>
                                    </button>
                                </div>
                                <HistoryManager 
                                    ref={historyManagerRef}
                                    logs={activityLogs}
                                    categories={categories}
                                    stockItems={stockItems}
                                    showFilters={showHistoryFilters}
                                    showGeminiInterface={showGeminiInterface}
                                    flashMessage={flashMessage}
                                    setError={setError}
                                    onImageClick={setViewingImage}
                                />
                            </>
                        )}
                        {activeTab === 'admin' && isAdmin && (
                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                                <div className="lg:col-span-1">
                                    <nav className="flex flex-col space-y-1">
                                        <AdminSubTabButton tabName="users" label="User Management" icon={Users} />
                                        <AdminSubTabButton tabName="categories" label="Category Management" icon={FolderTree} />
                                        <AdminSubTabButton tabName="data" label="Data Lists" icon={Library} />
                                    </nav>
                                </div>
                                <div className="lg:col-span-3">
                                    {adminSubTab === 'users' && (
                                        <UserManager 
                                            users={authorizedUsers}
                                            onAddUser={handleAddNewUser}
                                            onDeleteUser={handleDeleteUserClick}
                                            onSaveUser={handleSaveUser}
                                            currentUserEmail={user.email}
                                            onEditUser={setEditingUser}
                                            allCategories={categories}
                                        />
                                    )}
                                    {adminSubTab === 'categories' && (
                                        <CategoryManager categories={categories} stockItems={stockItems} categoryTree={categoryTree} onAdd={handleAddCategory} onUpdate={handleUpdateCategory} onDelete={handleDeleteCategoryClick} onSeed={seedCategories} isSeeding={isSeeding} />
                                    )}
                                    {adminSubTab === 'data' && (
                                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                            <Card title="Technicians" icon={UserPlus}>
                                                <AdvancedManager 
                                                    title="Technician" 
                                                    items={technicians}
                                                    onAdd={(name) => handleAddSimple('technicians', name)}
                                                    onEditItem={(item) => setEditingSimpleItem({ collectionName: 'technicians', item, title: 'Technician' })}
                                                    onDelete={(id) => handleDeleteSimpleClick('technicians', id)}
                                                    icon={UserPlus}
                                                />
                                            </Card>
                                            <Card title="Customers" icon={Building}>
                                                <AdvancedManager 
                                                    title="Customer" 
                                                    items={customers}
                                                    onAdd={(name) => handleAddSimple('customers', name)}
                                                    onEditItem={(item) => setEditingSimpleItem({ collectionName: 'customers', item, title: 'Customer' })}
                                                    onDelete={(id) => handleDeleteSimpleClick('customers', id)}
                                                    icon={Building}
                                                />
                                            </Card>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            {editingStockItem && (
                <EditStockItemModal
                    item={editingStockItem}
                    categoryTree={categoryTree}
                    onSave={handleUpdateStockItem}
                    onCancel={() => setEditingStockItem(null)}
                />
            )}
            {editingUser && (
                <EditUserModal 
                    user={editingUser}
                    onCancel={() => setEditingUser(null)}
                    onSaveUser={handleSaveUser}
                    onSendPasswordReset={handleSendPasswordReset}
                    allCategories={categories}
                />
            )}
            {editingSimpleItem && (
                <EditSimpleItemModal
                    item={editingSimpleItem.item}
                    title={editingSimpleItem.title}
                    onSave={(id, newName) => {
                        handleUpdateSimple(editingSimpleItem.collectionName, id, newName);
                        setEditingSimpleItem(null);
                    }}
                    onCancel={() => setEditingSimpleItem(null)}
                />
            )}
            {confirmState && (
                <ConfirmModal
                    title={confirmState.title}
                    message={confirmState.message}
                    onConfirm={confirmState.onConfirm}
                    onCancel={() => setConfirmState(null)}
                />
            )}
            {viewingImage && (
                <ImageViewerModal imageUrl={viewingImage} onClose={() => setViewingImage(null)} />
            )}
        </div>
    );
};

// --- Top-Level App Component ---
const App = () => {
    const [isTailwindReady, setIsTailwindReady] = useState(false);
    const [user, setUser] = useState(undefined);
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [storage, setStorage] = useState(null);
    const [showLoginSuccess, setShowLoginSuccess] = useState(false);
    const [appCheckError, setAppCheckError] = useState('');
    const prevUserRef = useRef(undefined);

    // --- Style and Script Injection ---
    useEffect(() => {
        // --- FIX for UNHANDLEDREJECTION null ---
        // This error is periodic (every 30-60s) and suggests
        // the App Check token auto-refresh is failing.
        // This is almost certainly because the reCAPTCHA v3
        // settings do not authorize the domain this app is running on.
        // This handler will silence the console error, but
        // the underlying issue (and related storage failures)
        // will persist until the reCAPTCHA settings are fixed.
        const handleRejection = (event) => {
            if (event.reason === null) {
                console.warn(
                    'Caught unhandled rejection: null. ' +
                    'This is likely the App Check token auto-refresh failing. ' +
                    'Please verify your reCAPTCHA v3 authorized domains in the Google Cloud console. ' +
                    'This app\'s domain must be on the allow-list for storage uploads to work.'
                );
                event.preventDefault();
            }
        };
        window.addEventListener('unhandledrejection', handleRejection);


        // Set browser tab title
        document.title = 'Trinet Inventory';

        // Create and set favicon from an SVG data URL to match the app's icon
        const faviconLink = document.querySelector("link[rel~='icon']") || document.createElement('link');
        faviconLink.rel = 'icon';
        faviconLink.href = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZHRoPSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0ZjRmZTUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS13YXJlaG91c2UtaWNvbiBsdWNpZGUtd2FyZWhvdXNlIj48cGF0aCBkPSJNMTggMjFWMTBhMSAxIDAgMCAwLTEtMUg3YTEgMSAwIDAgMC0xIDF2MTEiLz48cGF0aCBkPSJNMjIgMTlhMiAyIDAgMCAxLTIgMkg0YTIgMiAwIDAgMS0yLTJWOGEyIDIgMCAwIDEgMS4xMzItMS44MDNsNy45NS0zLjk3NGEyIDIgMCAwIDEgMS44MzcgMGw3Ljk0OCAzLjk3NEEyIDIgMCAwIDEgMjIgOHoiLz48cGF0aCBkPSJNNiAxM2gxMiIvPjxwYXRoIGQ9Ik02IDE3aDEyIi8+PC9zdmc+";
        document.head.appendChild(faviconLink);
        
        const scriptId = 'tailwind-cdn';
        const styleId = 'app-base-styles';
        const onTailwindReady = () => setIsTailwindReady(true);

        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = "https://cdn.tailwindcss.com";
            script.onload = onTailwindReady;
            document.head.appendChild(script);
        } else {
            onTailwindReady();
        }

        const scripts = [
            { id: 'xlsx', src: 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js' }
        ];

        scripts.forEach(scriptInfo => {
            if (!document.getElementById(scriptInfo.id)) {
                const script = document.createElement('script');
                script.id = scriptInfo.id;
                script.src = scriptInfo.src;
                document.head.appendChild(script);
            }
        });

        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                html, body, #root {
                    height: 100%;
                    width: 100%;
                    margin: 0;
                    padding: 0;
                    overflow-x: hidden;
                }
                :root { color-scheme: light !important; }
                input, textarea, select, option { color: #111827 !important; }
                @keyframes fade-in-scale {
                    0% { opacity: 0; transform: scale(0.95); }
                    100% { opacity: 1; transform: scale(1); }
                }
                .animate-fade-in-scale {
                    animation: fade-in-scale 0.2s ease-out forwards;
                }
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `;
            document.head.appendChild(style);
        }

        // Return cleanup function for the rejection handler
        return () => {
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);

    useEffect(() => {
        const app = initializeApp(firebaseConfig);
        let unsubAuth; // To store unsubscribe function

        // Define an async function to handle initialization
        const initializeFirebase = async () => {
            // --- APP CHECK INITIALIZATION ---
            // This is the fix for 'storage/unauthenticated' errors
            const recaptchaSiteKey = "6Ldx2e4rAAAAADpUAPCPAtIztGz2WCNn-dR-Gv-2";

            if (recaptchaSiteKey === "YOUR_RECAPTCHA_V3_SITE_KEY_GOES_HERE") {
                console.warn("App Check is NOT configured. Please add your reCAPTCHA v3 Site Key to App.jsx.");
                setAppCheckError("Security Error: App Check is not configured. Please add your reCAPTCHA v3 Site Key to App.jsx for uploads and database access to work.");
                return; // Stop initialization
            }

            try {
                // AWAIT the initialization of App Check
                await initializeAppCheck(app, {
                    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
                    isTokenAutoRefreshEnabled: true
                });
                console.log("App Check initialized successfully.");
                setAppCheckError(''); // Clear any previous error
            } catch (err) {
                console.error("Failed to initialize App Check:", err);
                setAppCheckError("Failed to initialize App Check. This may be due to an invalid reCAPTCHA key or domain settings.");
                return; // Stop initialization
            }
            // --- END OF APP CHECK FIX ---

            // NOW, initialize other services *after* App Check is ready
            try {
                const firebaseAuth = getAuth(app);
                const firestoreDb = getFirestore(app);
                const firebaseStorage = getStorage(app);

                setAuth(firebaseAuth);
                setDb(firestoreDb);
                setStorage(firebaseStorage);

                unsubAuth = onAuthStateChanged(firebaseAuth, (currentUser) => {
                    // --- FIX ---
                    // Added try/catch block to prevent unhandled rejections
                    // inside the auth state listener. These silent errors
                    // were breaking the App Check token auto-refresh.
                    try {
                        if (prevUserRef.current === null && currentUser) {
                            setShowLoginSuccess(true);
                        }
                        prevUserRef.current = currentUser;
                        setUser(currentUser);
                    } catch (err) {
                        console.error("Error within onAuthStateChanged listener:", err);
                        // This prevents the app from crashing due to a listener error.
                    }
                    // --- END FIX ---
                });
            } catch (err) {
                console.error("Failed to initialize Firebase services (Auth, Firestore, Storage):", err);
                setAppCheckError("Failed to initialize core Firebase services. The app cannot load.");
            }
        };

        // Call the async initialization function and add a catch block
        initializeFirebase().catch(err => {
             console.error("Unhandled error during Firebase initialization:", err);
             setAppCheckError("An unexpected error occurred during app startup.");
        });

        // Return the cleanup function for auth
        return () => {
            if (unsubAuth) {
                unsubAuth();
            }
        };
    }, []);

    if (user === undefined) {
        return <div className="flex items-center justify-center h-screen bg-gray-100"><div className="text-xl font-semibold text-gray-700">Loading...</div></div>;
    }

    if (appCheckError) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100 p-4">
                <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-xl shadow-lg text-center">
                    <AlertTriangle className="w-16 h-16 text-red-500 mx-auto" />
                    <h2 className="text-2xl font-bold text-gray-800 mt-4">Security Configuration Error</h2>
                    <p className="text-gray-600">{appCheckError}</p>
                    <p className="text-sm text-gray-500 mt-4">Please find your reCAPTCHA v3 Site Key in your Firebase Console (Project Settings / App Check) and paste it into the `recaptchaSiteKey` variable in App.jsx.</p>
                </div>
            </div>
        );
    }

    if (showLoginSuccess) {
        return <LoginSuccessMessage onFinish={() => setShowLoginSuccess(false)} />;
    }
    
    if (!isTailwindReady) {
        return <div className="flex items-center justify-center h-screen bg-gray-100">Loading styles...</div>;
    }

    return user ? <StockTrackerApp user={user} auth={auth} db={db} storage={storage} /> : <AuthPage auth={auth} />;
};

export default App;

